import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Switch,
  Tab,
  Tabs,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SourceRoundedIcon from '@mui/icons-material/SourceRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  createExpeditionBillingProcess,
  getApiErrorMessage,
  getExpeditionBillingPendingCustomers,
  getExpeditionBillingPendingSynchronization,
  getExpeditionBillingPendingPedidos,
  getExpeditionBillingPendingPedidoDetails,
  getExpeditionBillingCustomerConfigurations,
  getExpeditionBillingModels,
  getExpeditionBillingProcess,
  getExpeditionBillingProcessLines,
  getExpeditionBillingProposalCandidates,
  getExpeditionBillingLiquidations,
  downloadExpeditionBillingLiquidation,
  getExpeditionBillingProcessStatuses,
  getExpeditionBillingProcesses,
  createExpeditionBillingModel,
  deleteExpeditionBillingProcess,
  updateExpeditionBillingCustomerConfiguration,
  updateExpeditionBillingModel,
  uploadExpeditionBillingLiquidation,
  retryExpeditionBillingLiquidation,
  getExpeditionBillingProposal,
  generateExpeditionBillingProposal,
  createExpeditionBillingInvoicesInApplication,
  sendExpeditionBillingInvoicesToBusinessCentral,
  resendExpeditionBillingInvoiceToBusinessCentral,
  revertExpeditionBillingInvoiceCreation,
  updateExpeditionBillingProposalLineAllocations,
  updateExpeditionBillingProposalLinePrice,
  updateExpeditionBillingProposalLinePriceUnit,
  updateExpeditionBillingProposalLineManualPrice,
  getExpeditionBillingMappingCustomers,
  getExpeditionBillingProductAttributeValues,
  getExpeditionBillingCustomerDestinations,
  getExpeditionBillingProductAttributeMappings,
  createExpeditionBillingProductAttributeMapping,
  updateExpeditionBillingProductAttributeMapping,
  getExpeditionBillingDestinationMappings,
  createExpeditionBillingDestinationMapping,
  updateExpeditionBillingDestinationMapping,
  synchronizeExpeditionBillingPending,
  syncExpeditionBillingMasterData,
  getExpeditionBillingMasterDataSyncExecution,
} from '../services';

const PAGE_SIZE = 500;
const CONFIGURATION_PAGE_SIZE = 25;

function getCollection(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function getMeta(response) {
  return response?.meta || {};
}

function priceUnitLabel(value) {
  return value === 'KILOS' ? 'Kilos' : value === 'UNIDADES_VARIABLES' ? 'Unidades variables' : 'Sin definir';
}

function ManualProposalPriceEditor({ line, onSave, disabled }) {
  const [value, setValue] = useState(line.manualPrice ?? '');
  useEffect(() => setValue(line.manualPrice ?? ''), [line.id, line.manualPrice]);
  const unchanged = String(value) === String(line.manualPrice ?? '');
  const valid = value === '' || (Number.isFinite(Number(value)) && Number(value) >= 0);
  return <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.75 }}>
    <TextField
      size="small"
      type="number"
      label={`Precio manual · ${line.customerPriceUnit === 'KILOS' ? '€/kg' : '€/unidad variable'}`}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      inputProps={{ min: 0, step: 'any' }}
      sx={{ minWidth: 190, flex: 1 }}
    />
    <Button size="small" variant="outlined" onClick={() => onSave(line, value === '' ? null : Number(value))} disabled={disabled || unchanged || !valid} sx={{ minHeight: 40 }}>
      {disabled ? 'Guardando…' : 'Guardar'}
    </Button>
  </Stack>;
}

function formatNumber(value, maximumFractionDigits = 0) {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return new Intl.NumberFormat('es-ES', { maximumFractionDigits, useGrouping: 'always' }).format(number);
}

function formatCurrency(value) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', useGrouping: 'always' }).format(Number(value || 0));
}

function totalAmounts(items) {
  return items.reduce((total, item) => ({
    amount: total.amount + Number(item.estimatedAmount ?? item.estimatedPackagingAmount ?? item.packagingAmount ?? 0),
    packaging: total.packaging + Number(item.estimatedPackagingAmount ?? item.packagingAmount ?? 0),
    missing: total.missing + Number(item.unpricedLines ?? (item.estimatedAmount == null ? 1 : 0)),
  }), { amount: 0, packaging: 0, missing: 0 });
}

function MoneyAmount({ value, packaging = 0, missing = 0, align = 'flex-end' }) {
  const hasPackaging = Number(packaging) > 0;
  const fruitAmount = value == null ? null : Number(value) - Number(packaging);
  return <Stack spacing={0.15} alignItems={align} sx={{ minWidth: 0 }}>
    <Typography className="billing-money-value">{value == null ? 'Sin valorar' : formatCurrency(value)}</Typography>
    {hasPackaging ? <Typography className="billing-secondary-cell">Fruta {fruitAmount == null ? 'sin valorar' : formatCurrency(fruitAmount)}</Typography> : null}
    {hasPackaging ? <Typography className="billing-secondary-cell">Fianzas {formatCurrency(packaging)}</Typography> : null}
    {missing > 0 ? <Typography className="billing-money-warning">Parcial · {formatNumber(missing)} sin valorar</Typography> : null}
  </Stack>;
}

function formatDate(value) {
  if (!value) return '-';
  const normalized = String(value).length === 10 ? `${value}T12:00:00` : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

function normalizeText(value) {
  return String(value || '').trim().toLocaleLowerCase('es');
}

function getProposalHeaderMetadata(invoice) {
  const dates = [...new Set(invoice.lines.map((line) => line.fechaBl ? formatDate(line.fechaBl) : null).filter(Boolean))];
  const destinations = [...new Map(invoice.lines.filter((line) => line.destination).map((line) => {
    const internal = line.internalDestination?.name;
    const label = internal && normalizeText(internal) !== normalizeText(line.destination)
      ? `${line.destination} → ${internal}`
      : line.destination;
    return [normalizeText(line.destination), label];
  }))].map(([, label]) => label);
  return { dates, destinations };
}

function areInvoiceLinesAssigned(invoice) {
  return invoice.lines.length > 0 && invoice.lines.every((line) => line.matchStatus === 'ASIGNADA');
}

function ProposalHeaderMetadata({ invoice }) {
  const { dates, destinations } = getProposalHeaderMetadata(invoice);
  if (!dates.length && !destinations.length) return null;
  return <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ flex: 1, minWidth: 0, alignItems: 'center', px: 1.5, py: 0.75, borderLeft: '2px solid', borderColor: 'divider' }}>
    {dates.length ? <Box sx={{ minWidth: 115 }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.3 }}>FECHA BL</Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{dates.join(' · ')}</Typography>
    </Box> : null}
    {destinations.length ? <Box sx={{ flex: 1, minWidth: 180 }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.3 }}>DESTINO</Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, overflowWrap: 'anywhere' }}>{destinations.join(' · ')}</Typography>
    </Box> : null}
  </Stack>;
}

function PageHeading({ eyebrow, title, children, actions }) {
  return (
    <Box className="billing-page-heading">
      <Box>
        <Typography className="suite-page-eyebrow">{eyebrow}</Typography>
        <Typography component="h1" className="billing-page-title">{title}</Typography>
        {children ? <Typography className="billing-page-description">{children}</Typography> : null}
      </Box>
      {actions ? <Box className="billing-heading-actions">{actions}</Box> : null}
    </Box>
  );
}

function SectionTitle({ title, detail, count, children, className = '' }) {
  return (
    <Box className={`billing-section-title ${className}`}>
      <Box>
        <Typography component="h2" className="billing-section-heading">{title}</Typography>
        {detail ? <Typography className="billing-section-detail">{detail}</Typography> : null}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        {count !== undefined ? <Chip label={count} size="small" className="billing-count-chip" /> : null}
        {children}
      </Stack>
    </Box>
  );
}

function EmptyState({ icon: Icon = FolderOpenRoundedIcon, title, detail, action }) {
  return (
    <Box className="billing-empty-state">
      <Box className="billing-empty-icon"><Icon /></Box>
      <Typography className="billing-empty-title">{title}</Typography>
      {detail ? <Typography className="billing-empty-detail">{detail}</Typography> : null}
      {action || null}
    </Box>
  );
}

function TableFrame({ children, className = '' }) {
  return (
    <TableContainer component={Box} className={`billing-table-scroll ${className}`.trim()}>
      {children}
    </TableContainer>
  );
}

const PROCESS_STATUS_STEPS = [
  { code: 'PENDIENTE_LIQUIDACION', label: 'Pendiente de liquidación' },
  { code: 'ANALIZANDO_LIQUIDACION', label: 'Analizando liquidación' },
  { code: 'LIQUIDACION_ANALIZADA', label: 'Liquidación analizada' },
  { code: 'PROPUESTA_GENERADA', label: 'Propuesta generada' },
  { code: 'PROPUESTA_VALIDADA', label: 'Validada' },
  { code: 'FACTURADA', label: 'Facturada en Navision' },
];

const LEGACY_PROCESS_STATUS = {
  BORRADOR: 'PENDIENTE_LIQUIDACION',
  DRAFT: 'PENDIENTE_LIQUIDACION',
  ANALIZANDO: 'ANALIZANDO_LIQUIDACION',
  EN_REVISION: 'PROPUESTA_GENERADA',
  APROBADA: 'PROPUESTA_GENERADA',
  FACTURAS_CREADAS: 'PROPUESTA_VALIDADA',
  ENVIADA_A_BC: 'FACTURADA',
  CANCELADA: 'ERROR',
};

function normalizeProcessStatus(status) {
  const value = String(status || '');
  return LEGACY_PROCESS_STATUS[value] || value;
}

function StatusChip({ status, statuses = [] }) {
  const value = String(status || '');
  const normalized = normalizeProcessStatus(value);
  const definition = statuses.find((item) => item.code === normalized);
  const label = PROCESS_STATUS_STEPS.find((item) => item.code === normalized)?.label
    || (normalized === 'ERROR' ? 'Error' : definition?.label || normalized || '-');
  const tone = normalized === 'ERROR'
    ? 'billing-status-error'
    : normalized === 'PROPUESTA_VALIDADA' || normalized === 'FACTURADA'
      ? 'billing-status-success'
      : 'billing-status-pending';

  return <Chip label={label} size="small" className={`billing-status-chip ${tone}`} />;
}

function canDeleteProcess(status, statuses) {
  return Boolean(statuses.find((item) => item.code === status)?.canDelete);
}

function useProcessStatuses() {
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    let mounted = true;
    getExpeditionBillingProcessStatuses()
      .then((response) => {
        if (mounted) setStatuses(getCollection(response));
      })
      .catch(() => {
        if (mounted) setStatuses([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return statuses;
}

function PendingMetrics({ customers }) {
  const totals = useMemo(() => customers.reduce((result, customer) => ({
    expeditions: result.expeditions + Number(customer.expeditions || 0),
    lines: result.lines + Number(customer.lines || 0),
    bultos: result.bultos + Number(customer.bultos || 0),
  }), { expeditions: 0, lines: 0, bultos: 0 }), [customers]);
  const amounts = useMemo(() => totalAmounts(customers), [customers]);

  return (
    <Box className="billing-metric-strip" aria-label="Resumen de expediciones pendientes">
      <Metric label="Expediciones" value={formatNumber(totals.expeditions)} />
      <Metric label="Líneas" value={formatNumber(totals.lines)} />
      <Metric label="Bultos pendientes" value={formatNumber(totals.bultos, 2)} highlighted />
      <Metric label="Importe estimado" value={formatCurrency(amounts.amount)} detail={amounts.missing ? `${formatNumber(amounts.missing)} líneas sin valorar` : undefined} />
    </Box>
  );
}

function Metric({ label, value, detail, highlighted = false }) {
  return (
    <Box className={`billing-metric ${highlighted ? 'billing-metric-highlighted' : ''}`}>
      <Typography className="billing-metric-value">{value}</Typography>
      <Typography className="billing-metric-label">{label}</Typography>
      {detail ? <Typography className="billing-money-warning">{detail}</Typography> : null}
    </Box>
  );
}

export function PendingExpeditionsPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [query, setQuery] = useState('');

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getExpeditionBillingPendingCustomers({ page: 1, limit: PAGE_SIZE });
      setCustomers(getCollection(response));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron cargar las expediciones pendientes.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const synchronize = async () => {
    setSyncing(true);
    setSyncMessage('');
    setError('');
    try {
      const result = await synchronizeExpeditionBillingPending();
      let execution = result;
      let polls = 0;
      while (['PENDING', 'RUNNING'].includes(execution?.status) && polls < 450) {
        setSyncMessage(execution.status === 'PENDING' ? 'Sincronización en cola…' : 'Sincronizando con Dynamics…');
        await new Promise((resolve) => setTimeout(resolve, 2000));
        execution = await getExpeditionBillingPendingSynchronization(result.executionId);
        polls += 1;
      }
      if (['PENDING', 'RUNNING'].includes(execution?.status)) {
        throw new Error('La sincronización sigue en curso. Espera un poco y consulta de nuevo antes de volver a iniciarla.');
      }
      if (execution?.status === 'FAILED') {
        throw new Error(execution.errorSummary || 'La sincronización ha fallado.');
      }
      await loadCustomers();
      const read = Number(execution?.recordsRead ?? 0);
      const failed = Number(execution?.recordsFailed ?? 0);
      setSyncMessage(failed > 0
        ? `Sincronización parcial: ${formatNumber(read)} registros leídos y ${formatNumber(failed)} con error.`
        : `Sincronización completada: ${formatNumber(read)} registros leídos.`);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron sincronizar las expediciones con Dynamics.'));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const visibleCustomers = useMemo(() => {
    const term = normalizeText(query);
    if (!term) return customers;
    return customers.filter((customer) =>
      [customer.customerCode, customer.customerName].some((value) => normalizeText(value).includes(term))
    );
  }, [customers, query]);

  return (
    <Box className="billing-page">
      <PageHeading
        eyebrow="Facturación de expediciones"
        title="Expediciones pendientes"
      >
        Expediciones de venta disponibles para iniciar su proceso de facturación.
      </PageHeading>

      <Paper elevation={0} className="billing-band">
        <PendingMetrics customers={customers} />
      </Paper>

      <Paper elevation={0} className="billing-band billing-data-band">
        <SectionTitle
          className="billing-pending-section-title"
          title="Clientes con expediciones pendientes"
          detail="Clientes con expediciones que aún se pueden facturar."
        >
          <Box className="billing-pending-actions">
            <TextField
              className="billing-search-field"
              placeholder="Buscar cliente"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={15} /> : <RefreshRoundedIcon />}
              onClick={synchronize}
              disabled={syncing || loading}
              sx={{ minHeight: 40 }}
            >
              {syncing ? 'Sincronizando…' : 'Sincronizar con Dynamics'}
            </Button>
          </Box>
        </SectionTitle>

        {error ? <Alert severity="error" className="billing-alert">{error}</Alert> : null}
        {syncMessage ? <Alert severity={syncing ? 'info' : 'success'} className="billing-alert">{syncMessage}</Alert> : null}
        {loading ? <LinearProgress /> : null}

        {!loading && !error && visibleCustomers.length === 0 ? (
          <EmptyState
            title={query ? 'No hay coincidencias' : 'No hay expediciones pendientes'}
            detail={query ? 'Prueba con otro código o nombre de cliente.' : 'Las líneas facturables aparecerán aquí cuando estén sincronizadas.'}
          />
        ) : null}

        {visibleCustomers.length > 0 ? (
          <TableFrame>
            <Table className="billing-table billing-customers-table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell align="right">Expediciones</TableCell>
                  <TableCell align="right">Líneas</TableCell>
                  <TableCell align="right">Bultos pendientes</TableCell>
                  <TableCell align="right">Importe estimado</TableCell>
                  <TableCell align="right" aria-label="Acciones" />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCustomers.map((customer) => (
                  <TableRow
                    key={customer.customerCode}
                    hover
                    className="billing-clickable-row"
                    onClick={() => navigate(`/pendientes/${encodeURIComponent(customer.customerCode)}`)}
                  >
                    <TableCell>
                      <Typography className="billing-primary-cell">{customer.customerName || customer.customerCode}</Typography>
                      <Typography className="billing-secondary-cell">{customer.customerCode}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(customer.expeditions)}</TableCell>
                    <TableCell align="right">{formatNumber(customer.lines)}</TableCell>
                    <TableCell align="right" className="billing-number-strong">{formatNumber(customer.bultos, 2)}</TableCell>
                    <TableCell align="right"><MoneyAmount value={customer.estimatedAmount} packaging={customer.packagingAmount} missing={customer.unpricedLines} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver expediciones del cliente">
                        <IconButton aria-label={`Ver expediciones de ${customer.customerName || customer.customerCode}`} size="small">
                          <ArrowForwardRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableFrame>
        ) : null}
      </Paper>
    </Box>
  );
}

export function PendingCustomerPage() {
  const { customerCode } = useParams();
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPedidos, setSelectedPedidos] = useState([]);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [selectionDetails, setSelectionDetails] = useState([]);
  const [loadingSelectionDetails, setLoadingSelectionDetails] = useState(false);
  const [pedidoSearch, setPedidoSearch] = useState('');
  const [pedidoDateField, setPedidoDateField] = useState('orderDate');
  const [pedidoDateFrom, setPedidoDateFrom] = useState('');
  const [pedidoDateTo, setPedidoDateTo] = useState('');

  const loadPedidos = useCallback(async (search = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await getExpeditionBillingPendingPedidos({ customerCode, page: 1, limit: PAGE_SIZE, search });
      setPedidos(getCollection(response));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron cargar los pedidos pendientes.'));
    } finally {
      setLoading(false);
    }
  }, [customerCode]);

  useEffect(() => {
    const timer = window.setTimeout(() => loadPedidos(pedidoSearch), pedidoSearch ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [loadPedidos, pedidoSearch]);

  const customerName = pedidos[0]?.customerName || customerCode;
  const filteredPedidos = pedidos.filter((pedido) => {
    if (!pedidoDateFrom && !pedidoDateTo) return true;
    const date = String(pedido[pedidoDateField] || '').slice(0, 10);
    if (!date) return false;
    return (!pedidoDateFrom || date >= pedidoDateFrom) && (!pedidoDateTo || date <= pedidoDateTo);
  });
  const pedidoKey = (pedido) => pedido.missingPedido ? '__EMPTY__' : pedido.pedidoExpedicion;
  const selectedPedidoKeys = new Set(selectedPedidos.map(pedidoKey));
  const selectedPedidoCodes = selectedPedidos.map((pedido) => pedido.pedidoExpedicion).filter(Boolean).join(',');
  const includesMissingPedido = selectedPedidos.some((pedido) => pedido.missingPedido);
  useEffect(() => {
    if (!selectionDialogOpen) return undefined;
    let active = true;
    setLoadingSelectionDetails(true);
    getExpeditionBillingPendingPedidoDetails({
      customerCode,
      pedidoExpediciones: selectedPedidoCodes,
      includeMissingPedido: String(includesMissingPedido),
    }).then((response) => {
      if (active) setSelectionDetails(getCollection(response));
    }).catch((requestError) => {
      if (active) setDialogError(getApiErrorMessage(requestError, 'No se pudieron cargar las líneas de los pedidos.'));
    }).finally(() => {
      if (active) setLoadingSelectionDetails(false);
    });
    return () => { active = false; };
  }, [customerCode, includesMissingPedido, selectedPedidoCodes, selectionDialogOpen]);
  const togglePedido = (pedido) => setSelectedPedidos((current) => (
    current.some((selected) => pedidoKey(selected) === pedidoKey(pedido))
      ? current.filter((selected) => pedidoKey(selected) !== pedidoKey(pedido))
      : [...current, pedido]
  ));
  const selectedTotals = selectedPedidos.reduce((totals, pedido) => ({
    expeditions: totals.expeditions + Number(pedido.expeditions || 0),
    lines: totals.lines + Number(pedido.lines || 0),
    bultos: totals.bultos + Number(pedido.bultos || 0),
  }), { expeditions: 0, lines: 0, bultos: 0 });
  const filteredAmounts = totalAmounts(filteredPedidos);
  const selectedAmounts = totalAmounts(selectedPedidos);
  const createProcess = async () => {
    if (selectedPedidos.length === 0) return;
    const completedPedidos = [];
    setCreating(true);
    setDialogError('');
    try {
      for (const pedido of selectedPedidos) {
        await createExpeditionBillingProcess({
          customerCode,
          pedidoExpedicion: pedido.missingPedido ? null : pedido.pedidoExpedicion,
        });
        completedPedidos.push(pedido.missingPedido ? 'Sin pedido informado' : pedido.pedidoExpedicion);
      }
      navigate('/procesos', {
        replace: true,
        state: { batchCreated: selectedPedidos.map((pedido) => pedido.missingPedido ? 'Sin pedido informado' : pedido.pedidoExpedicion) },
      });
    } catch (requestError) {
      const apiMessage = getApiErrorMessage(requestError, 'No se pudo crear el proceso de facturación.');
      setDialogError(completedPedidos.length
        ? `Se procesaron ${completedPedidos.length} de ${selectedPedidos.length} pedidos (${completedPedidos.join(', ')}). Cada uno requiere su propio PDF. ${apiMessage}`
        : apiMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box className="billing-page">
      <Breadcrumbs className="billing-breadcrumbs" aria-label="Miga de pan">
        <Typography component={RouterLink} to="/">Expediciones pendientes</Typography>
        <Typography color="text.primary">{customerName}</Typography>
      </Breadcrumbs>
      <PageHeading
        eyebrow="Cliente"
        title={customerName}
      >
        {customerCode}
      </PageHeading>

      <Paper elevation={0} className="billing-band billing-data-band">
        <SectionTitle
          title="Pedidos pendientes"
          count={`${formatNumber(filteredPedidos.length)} pedidos`}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <TextField
              className="billing-search-field"
              size="small"
              placeholder="Filtrar por pedido"
              value={pedidoSearch}
              onChange={(event) => setPedidoSearch(event.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 175 }}>
              <InputLabel id="pedido-date-field-label">Filtrar fecha por</InputLabel>
              <Select labelId="pedido-date-field-label" label="Filtrar fecha por" value={pedidoDateField} onChange={(event) => setPedidoDateField(event.target.value)}>
                <MenuItem value="orderDate">Fecha de carga</MenuItem>
                <MenuItem value="shipmentDate">Fecha de envío</MenuItem>
              </Select>
            </FormControl>
            <TextField id="pending-order-date-from" label="Desde" size="small" type="date" value={pedidoDateFrom} onChange={(event) => setPedidoDateFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 155 }} />
            <TextField id="pending-order-date-to" label="Hasta" size="small" type="date" value={pedidoDateTo} onChange={(event) => setPedidoDateTo(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 155 }} />
            <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={selectedPedidos.length === 0} onClick={() => { setDialogError(''); setSelectionDialogOpen(true); }} sx={{ whiteSpace: 'nowrap', minWidth: 160, height: 40 }}>
              Crear proceso
            </Button>
          </Stack>
        </SectionTitle>
        {error ? <Alert severity="error" className="billing-alert">{error}</Alert> : null}
        {loading ? <LinearProgress /> : null}
        {!loading && !error && pedidos.length === 0 ? (
          <EmptyState title="No hay pedidos pendientes" detail="No se encontraron líneas de expedición no facturadas para este cliente." />
        ) : null}
        {filteredPedidos.length > 0 ? (
          <>
          <TableFrame>
            <Table className="billing-table billing-pedidos-table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pedido expedición</TableCell>
                  <TableCell>Fecha de carga</TableCell>
                  <TableCell>Fecha de envío</TableCell>
                  <TableCell align="right">Expediciones</TableCell>
                  <TableCell align="right">Líneas</TableCell>
                  <TableCell align="right">Bultos pendientes</TableCell>
                  <TableCell align="right">Importe estimado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPedidos.map((pedido, index) => (
                  <TableRow key={`${pedido.pedidoExpedicion || 'sin-pedido'}-${index}`} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox checked={selectedPedidoKeys.has(pedidoKey(pedido))} onChange={() => togglePedido(pedido)} onClick={(event) => event.stopPropagation()} inputProps={{ 'aria-label': 'Seleccionar pedido ' + (pedido.pedidoExpedicion || 'sin pedido') }} />
                        <Typography className="billing-primary-cell">{pedido.missingPedido ? 'SIN PEDIDO' : pedido.pedidoExpedicion}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(pedido.orderDate)}</TableCell>
                    <TableCell>{formatDate(pedido.shipmentDate)}</TableCell>
                    <TableCell align="right">{formatNumber(pedido.expeditions)}</TableCell>
                    <TableCell align="right">{formatNumber(pedido.lines)}</TableCell>
                    <TableCell align="right" className="billing-number-strong">{formatNumber(pedido.bultos, 2)}</TableCell>
                    <TableCell align="right"><MoneyAmount value={pedido.estimatedAmount} packaging={pedido.packagingAmount} missing={pedido.unpricedLines} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableFrame>
          <Box className="billing-table-total"><Typography>Total de pedidos visibles</Typography><MoneyAmount value={filteredAmounts.amount} packaging={filteredAmounts.packaging} missing={filteredAmounts.missing} /></Box>
          </>
        ) : !loading && pedidos.length > 0 ? <EmptyState title="No hay pedidos en estas fechas" detail="Cambia el rango o el tipo de fecha para ver otros pedidos." /> : null}
      </Paper>

      <Dialog open={selectionDialogOpen && selectedPedidos.length > 0} onClose={() => !creating && setSelectionDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Crear proceso de facturación</DialogTitle>
        <DialogContent dividers>
          {dialogError ? <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert> : null}
          <Typography className="billing-dialog-lead">
            Se creará un proceso independiente por cada pedido. Cada proceso tendrá su propia propuesta y deberá llevar su archivo de liquidación.
          </Typography>
          <Box className="billing-dialog-summary">
            <SummaryItem label="Cliente" value={customerName} />
            <SummaryItem label="Procesos" value={selectedPedidos.length} />
            <SummaryItem label="Expediciones" value={formatNumber(selectedTotals.expeditions)} />
            <SummaryItem label="Líneas" value={formatNumber(selectedTotals.lines)} />
            <SummaryItem label="Bultos" value={formatNumber(selectedTotals.bultos, 2)} />
            <SummaryItem label="Importe estimado" value={formatCurrency(selectedAmounts.amount)} />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography className="billing-summary-label">Pedidos</Typography>
              <Typography className="billing-summary-value">{selectedPedidos.map((pedido) => pedido.missingPedido ? 'SIN PEDIDO' : pedido.pedidoExpedicion).join(', ')}</Typography>
            </Box>
          </Box>
          {loadingSelectionDetails ? <LinearProgress sx={{ mt: 2 }} /> : null}
          <Box className="billing-selection-details">
            {selectionDetails.map((order, orderIndex) => (
              <TableFrame key={order.pedidoExpedicion || `missing-${orderIndex}`}>
                <Table className="billing-selection-order-table" size="small">
                  <TableHead><TableRow><TableCell>Pedido expedición</TableCell></TableRow></TableHead>
                  <TableBody><TableRow><TableCell>
                    <Typography className="billing-primary-cell billing-selection-order-title">{order.missingPedido ? 'SIN PEDIDO' : order.pedidoExpedicion}</Typography>
                    <Table className="billing-selection-expedition-table" size="small">
                      <TableHead><TableRow><TableCell>Expedición</TableCell><TableCell>Fecha de carga</TableCell><TableCell>Fecha de envío</TableCell><TableCell>Detalle de líneas</TableCell></TableRow></TableHead>
                      <TableBody>{order.expeditions.map((expedition) => (
                        <TableRow key={expedition.expeditionNo}>
                          <TableCell className="billing-primary-cell">{expedition.expeditionNo}</TableCell>
                          <TableCell>{formatDate(expedition.orderDate)}</TableCell>
                          <TableCell>{formatDate(expedition.shipmentDate)}</TableCell>
                          <TableCell>
                            <TableFrame className="billing-selection-lines-scroll">
                            <Table className="billing-selection-lines-table" size="small">
                              <TableHead><TableRow>
                                <TableCell>Línea</TableCell><TableCell>Variedad</TableCell><TableCell>Confección</TableCell><TableCell>Calibre</TableCell><TableCell>Tipo de caja</TableCell><TableCell align="right">Palets</TableCell><TableCell align="right">Bultos por palet</TableCell><TableCell align="right">Bultos totales</TableCell>
                              </TableRow></TableHead>
                              <TableBody>{expedition.lines.map((line) => (
                                <TableRow key={`${expedition.expeditionNo}-${line.lineNo}`}>
                                  <TableCell>{line.lineNo}</TableCell><TableCell>{line.variety || '-'}</TableCell><TableCell>{line.confection || '-'}</TableCell><TableCell>{line.caliber || '-'}</TableCell><TableCell>{line.boxType || '-'}</TableCell><TableCell align="right">{formatNumber(line.pallets, 2)}</TableCell><TableCell align="right">{formatNumber(line.packagesPerPallet)}</TableCell><TableCell align="right" className="billing-number-strong">{formatNumber(line.packages, 3)}</TableCell>
                                </TableRow>
                              ))}</TableBody>
                            </Table>
                            </TableFrame>
                          </TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  </TableCell></TableRow></TableBody>
                </Table>
              </TableFrame>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectionDialogOpen(false)} disabled={creating}>Cancelar</Button>
          <Button variant="contained" onClick={createProcess} disabled={creating || loadingSelectionDetails || selectionDetails.length === 0} startIcon={creating ? <CircularProgress size={15} /> : <AddRoundedIcon />} sx={{ whiteSpace: 'nowrap', minWidth: 160, minHeight: 42 }}>
            Crear proceso
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SummaryItem({ label, value }) {
  return (
    <Box>
      <Typography className="billing-summary-label">{label}</Typography>
      <Typography className="billing-summary-value">{value || '-'}</Typography>
    </Box>
  );
}

export function BillingProcessesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const statuses = useProcessStatuses();
  const [deletingId, setDeletingId] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const loadProcesses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getExpeditionBillingProcesses({ page: 1, limit: PAGE_SIZE });
      setProcesses(getCollection(response));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron cargar los procesos de facturación.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  const visibleProcesses = useMemo(() => {
    const term = normalizeText(query);
    return processes.filter((process) => {
      const matchesSearch = !term || [process.id, process.customerCode, process.customerName, process.pedidoExpedicion, ...(process.pedidoExpediciones || []), process.status]
        .some((value) => normalizeText(value).includes(term));
      return matchesSearch && (!statusFilter || process.status === statusFilter);
    });
  }, [processes, query, statusFilter]);

  const deleteProcess = async (event, process) => {
    event.stopPropagation();
    setDeleteConfirmation(process);
  };

  const confirmDeleteProcess = async () => {
    const process = deleteConfirmation;
    if (!process) return;
    setDeletingId(process.id);
    setError('');
    try {
      await deleteExpeditionBillingProcess(process.id);
      setProcesses((current) => current.filter((item) => item.id !== process.id));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo eliminar el proceso de facturación.'));
    } finally {
      setDeletingId('');
      setDeleteConfirmation(null);
    }
  };

  return (
    <Box className="billing-page">
      <PageHeading
        eyebrow="Facturación de expediciones"
        title="Procesos de facturación"
      >
        Pedidos preparados para revisar y comparar con la liquidación del cliente.
      </PageHeading>

      <Paper elevation={0} className="billing-band billing-data-band">
        <SectionTitle title="Procesos registrados" count={`${formatNumber(visibleProcesses.length)} procesos`}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              className="billing-search-field"
              placeholder="Buscar cliente o pedido"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="process-status-filter-label">Estado</InputLabel>
              <Select
                labelId="process-status-filter-label"
                label="Estado"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <MenuItem value="">Todos los estados</MenuItem>
                {statuses.map((status) => <MenuItem key={status.code} value={status.code}>{status.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </SectionTitle>
        {error ? <Alert severity="error" className="billing-alert">{error}</Alert> : null}
        {location.state?.batchCreated?.length ? (
          <Alert severity="success" className="billing-alert">
            Se creó un proceso por pedido ({location.state.batchCreated.length}). Sube el PDF de liquidación correspondiente en cada proceso: {location.state.batchCreated.join(', ')}.
          </Alert>
        ) : null}
        {loading ? <LinearProgress /> : null}
        {!loading && !error && visibleProcesses.length === 0 ? (
          <EmptyState icon={SourceRoundedIcon} title={query || statusFilter ? 'No hay coincidencias' : 'Aún no hay procesos'} detail={query || statusFilter ? 'Prueba con otra búsqueda o cambia el estado.' : 'Los procesos creados desde expediciones pendientes aparecerán aquí.'} />
        ) : null}
        {visibleProcesses.length > 0 ? (
          <TableFrame>
            <Table className="billing-table billing-processes-table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Proceso</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Pedido</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Expediciones</TableCell>
                  <TableCell align="right">Líneas</TableCell>
                  <TableCell align="right">Bultos</TableCell>
                  <TableCell>Creado</TableCell>
                  <TableCell align="right" aria-label="Acciones" />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleProcesses.map((process) => (
                  <TableRow key={process.id} hover className="billing-clickable-row" onClick={() => navigate(`/procesos/${process.id}`)}>
                    <TableCell><Typography className="billing-primary-cell">#{process.id}</Typography></TableCell>
                    <TableCell><Typography className="billing-primary-cell">{process.customerName || process.customerCode}</Typography><Typography className="billing-secondary-cell">{process.customerCode}</Typography></TableCell>
                    <TableCell>
                      {process.pedidoCount > 1 ? (
                        <Tooltip title={(process.pedidoExpediciones || []).join(', ')}>
                          <Chip label={`${process.pedidoCount} pedidos`} size="small" />
                        </Tooltip>
                      ) : process.missingPedido ? 'SIN PEDIDO' : process.pedidoExpedicion || '-'}
                    </TableCell>
                    <TableCell><StatusChip status={process.status} statuses={statuses} /></TableCell>
                    <TableCell align="right">{formatNumber(process.snapshotExpeditions)}</TableCell>
                    <TableCell align="right">{formatNumber(process.snapshotLines)}</TableCell>
                    <TableCell align="right" className="billing-number-strong">{formatNumber(process.sourceBultosTotal, 2)}</TableCell>
                    <TableCell>{formatDate(process.createdAt)}</TableCell>
                    <TableCell align="right">
                      {canDeleteProcess(process.status, statuses) ? (
                        <Tooltip title="Eliminar proceso">
                          <span>
                            <IconButton aria-label={`Eliminar proceso ${process.id}`} size="small" color="error" disabled={deletingId === process.id} onClick={(event) => deleteProcess(event, process)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      <IconButton aria-label={`Abrir proceso ${process.id}`} size="small"><ArrowForwardRoundedIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableFrame>
        ) : null}
      </Paper>
      <Dialog open={Boolean(deleteConfirmation)} onClose={() => !deletingId && setDeleteConfirmation(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar proceso</DialogTitle>
        <DialogContent dividers><Typography>¿Eliminar el proceso #{deleteConfirmation?.id}? Sus líneas volverán a estar pendientes de facturar.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteConfirmation(null)} disabled={Boolean(deletingId)}>Cancelar</Button><Button color="error" variant="contained" onClick={confirmDeleteProcess} disabled={Boolean(deletingId)}>{deletingId ? 'Eliminando...' : 'Eliminar proceso'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export function BillingProcessDetailPage() {
  const { processId } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const [lines, setLines] = useState([]);
  const [candidateLines, setCandidateLines] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const liquidationsRef = useRef([]);
  const [proposal, setProposal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [snapshotFilters, setSnapshotFilters] = useState({ variedad: '', calibre: '', confeccion: '', envase: '', origen: '', destino: '', estado: '' });
  const [snapshotSort, setSnapshotSort] = useState({ key: 'expedicion', direction: 'asc' });
  const statuses = useProcessStatuses();
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retryingId, setRetryingId] = useState('');
  const [downloadingId, setDownloadingId] = useState('');
  const [generatingProposal, setGeneratingProposal] = useState(false);
  const [regenerateProposalConfirmationOpen, setRegenerateProposalConfirmationOpen] = useState(false);
  const [creatingInvoices, setCreatingInvoices] = useState(false);
  const [savingManualPriceId, setSavingManualPriceId] = useState('');
  const [createInvoicesDialogOpen, setCreateInvoicesDialogOpen] = useState(false);
  const [revertInvoicesDialogOpen, setRevertInvoicesDialogOpen] = useState(false);
  const [revertingInvoices, setRevertingInvoices] = useState(false);
  const [sendingInvoicesToBc, setSendingInvoicesToBc] = useState(false);
  const [sendingInvoiceId, setSendingInvoiceId] = useState('');
  const [bcSendSummary, setBcSendSummary] = useState('');
  const [customerDestinations, setCustomerDestinations] = useState([]);
  const [destinationMappings, setDestinationMappings] = useState({});
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [openCommandes, setOpenCommandes] = useState({});
  const [commandeFilter, setCommandeFilter] = useState('');
  const [proposalStatusFilter, setProposalStatusFilter] = useState('');
  const [assignmentDialog, setAssignmentDialog] = useState(null);

  const loadProcess = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const [processResponse, linesResponse, liquidationsResponse, proposalResponse, candidateLinesResponse] = await Promise.all([
        getExpeditionBillingProcess(processId),
        getExpeditionBillingProcessLines(processId, { page: 1, limit: PAGE_SIZE }),
        getExpeditionBillingLiquidations(processId),
        getExpeditionBillingProposal(processId),
        getExpeditionBillingProposalCandidates(processId),
      ]);
      setProcess(processResponse?.data || processResponse);
      setLines(getCollection(linesResponse));
      const nextLiquidations = getCollection(liquidationsResponse);
      liquidationsRef.current = nextLiquidations;
      setLiquidations(nextLiquidations);
      setProposal(getCollection(proposalResponse));
      setCandidateLines(getCollection(candidateLinesResponse));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo cargar el proceso de facturación.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [processId]);

  const refreshLiquidations = useCallback(async () => {
    try {
      const response = await getExpeditionBillingLiquidations(processId);
      const nextLiquidations = getCollection(response);
      const isAnalyzing = (liquidation) => ['PENDIENTE_ANALISIS', 'ANALIZANDO_OCR', 'ANALIZANDO'].includes(liquidation.status);
      const wasInProgress = liquidationsRef.current.some(isAnalyzing);
      const isInProgress = nextLiquidations.some(isAnalyzing);
      liquidationsRef.current = nextLiquidations;
      setLiquidations(nextLiquidations);
      if (wasInProgress && !isInProgress) await loadProcess({ silent: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo consultar el estado del análisis.'));
    }
  }, [processId, loadProcess]);

  const uploadLiquidation = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await uploadExpeditionBillingLiquidation(processId, file);
      await loadProcess();
      await refreshLiquidations();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo subir la liquidación.'));
    } finally {
      setUploading(false);
    }
  };

  const downloadLiquidationFile = async (liquidation) => {
    setDownloadingId(liquidation.id);
    setError('');
    try {
      const blob = await downloadExpeditionBillingLiquidation(processId, liquidation.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = liquidation.file?.originalName || 'liquidacion.pdf';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo descargar la liquidación.'));
    } finally {
      setDownloadingId('');
    }
  };

  const retryLiquidation = async (liquidationId) => {
    setRetryingId(liquidationId);
    setError('');
    try {
      await retryExpeditionBillingLiquidation(liquidationId);
      await loadProcess();
      await refreshLiquidations();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo reintentar el análisis.'));
    } finally {
      setRetryingId('');
    }
  };

  const submitGenerateProposal = async () => {
    setGeneratingProposal(true);
    setError('');
    try {
      await generateExpeditionBillingProposal(processId);
      setRegenerateProposalConfirmationOpen(false);
      await loadProcess();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo generar la propuesta de facturación.'));
    } finally {
      setGeneratingProposal(false);
    }
  };

  const generateProposal = () => {
    if (!liquidations.some((liquidation) => liquidation.status === 'ANALIZADA')) {
      setError('Debe haber una liquidación analizada antes de generar la propuesta.');
      return;
    }
    if (proposal.length > 0) {
      setRegenerateProposalConfirmationOpen(true);
      return;
    }
    submitGenerateProposal();
  };

  const createInvoices = async () => {
    setCreatingInvoices(true);
    setError('');
    try {
      await createExpeditionBillingInvoicesInApplication(processId, {
        destinationMappings: unresolvedInvoiceDestinations.map((item) => ({
          externalDestination: item.externalDestination,
          customerDestinationId: destinationMappings[item.key],
        })),
      });
      setCreateInvoicesDialogOpen(false);
      await loadProcess();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron crear las facturas en la aplicacion.'));
    } finally {
      setCreatingInvoices(false);
    }
  };

  const selectProposalPrice = async (line, selectedPriceSource) => {
    setError('');
    try {
      await updateExpeditionBillingProposalLinePrice(line.id, { selectedPriceSource });
      await loadProcess({ silent: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar el precio seleccionado.'));
    }
  };

  const selectProposalPriceUnit = async (line, customerPriceUnit) => {
    if (!customerPriceUnit || customerPriceUnit === line.customerPriceUnit) return;
    setError('');
    try {
      await updateExpeditionBillingProposalLinePriceUnit(line.id, { customerPriceUnit });
      await loadProcess({ silent: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar la unidad del precio cliente.'));
    }
  };

  const saveManualProposalPrice = async (line, manualPrice) => {
    setSavingManualPriceId(line.id);
    setError('');
    try {
      await updateExpeditionBillingProposalLineManualPrice(line.id, { manualPrice });
      await loadProcess({ silent: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar el precio manual.'));
    } finally {
      setSavingManualPriceId('');
    }
  };

  const revertInvoiceCreation = async () => {
    setRevertingInvoices(true);
    setError('');
    try {
      await revertExpeditionBillingInvoiceCreation(processId);
      setRevertInvoicesDialogOpen(false);
      await loadProcess();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo reabrir la propuesta.'));
    } finally {
      setRevertingInvoices(false);
    }
  };

  const sendInvoicesToBc = async () => {
    setSendingInvoicesToBc(true);
    setBcSendSummary('');
    setError('');
    try {
      const result = await sendExpeditionBillingInvoicesToBusinessCentral(processId);
      const summary = result?.data ?? result;
      if (summary?.phase === 'PENDIENTE_ENVIO') {
        setBcSendSummary(`Rechazos preparados en Business Central. Registra manualmente el envio de la expedicion original y de ${summary.rejectionExpeditions?.join(', ') || 'las expediciones de rechazo'}. Despues vuelve a pulsar Facturar. No se ha creado ninguna factura.`);
        await loadProcess({ silent: true });
        return;
      }
      const failedInvoices = (summary?.invoices ?? []).filter((invoice) => invoice.status === 'ERROR');
      const failures = failedInvoices.slice(0, 3).map((invoice) =>
        `Commande ${invoice.commandeNo}: ${invoice.error || 'Error sin detalle devuelto por Business Central'}`,
      );
      const moreFailures = failedInvoices.length > failures.length ? ` (+${failedInvoices.length - failures.length} errores más)` : '';
      setBcSendSummary(summary?.failed
        ? `Facturas creadas en BC: ${summary.created}. Pendientes por error: ${summary.failed}. ${failures.join(' · ')}${moreFailures}`
        : `Se han creado ${summary?.created ?? 0} facturas abiertas en Business Central. No se han contabilizado.`);
      await loadProcess({ silent: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron enviar las facturas a Business Central.'));
      await loadProcess({ silent: true });
    } finally {
      setSendingInvoicesToBc(false);
    }
  };

  const resendInvoiceToBc = async (invoice) => {
    setSendingInvoiceId(String(invoice.id));
    setBcSendSummary('');
    setError('');
    try {
      const result = await resendExpeditionBillingInvoiceToBusinessCentral(processId, invoice.id);
      const summary = result?.data ?? result;
      const sentInvoice = summary?.invoices?.find((item) => String(item.id) === String(invoice.id));
      setBcSendSummary(sentInvoice?.status === 'CREADA_EN_BC'
        ? `Factura ${sentInvoice.businessCentralInvoiceNo || ''} de la commande ${invoice.commandeNo} enviada a Business Central.`
        : `No se pudo enviar la factura de la commande ${invoice.commandeNo}: ${sentInvoice?.error || 'Business Central no devolvió el detalle del error.'}`);
      await loadProcess({ silent: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo enviar la factura a Business Central.'));
      await loadProcess({ silent: true });
    } finally {
      setSendingInvoiceId('');
    }
  };

  const saveManualAllocation = async (proposalLineId, payload) => {
    await updateExpeditionBillingProposalLineAllocations(proposalLineId, payload);
    await loadProcess({ silent: true });
  };

  useEffect(() => {
    loadProcess();
  }, [loadProcess]);

  const liquidationAnalysisInProgress = liquidations.some((liquidation) => [
    'PENDIENTE_ANALISIS',
    'ANALIZANDO_OCR',
    'ANALIZANDO',
  ].includes(liquidation.status));

  useEffect(() => {
    if (!liquidationAnalysisInProgress) return undefined;
    let active = true;
    let timer;
    const poll = async () => {
      await refreshLiquidations();
      if (active) timer = window.setTimeout(poll, 2500);
    };
    timer = window.setTimeout(poll, 2500);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [liquidationAnalysisInProgress, refreshLiquidations]);

  const visibleLines = useMemo(() => {
    const term = normalizeText(query);
    const filtered = lines.filter((line) => {
      const attributeFiltersMatch = ['variedad', 'calibre', 'confeccion', 'envase', 'origen', 'destino'].every((attribute) => {
        if (!snapshotFilters[attribute]) return true;
        const value = attribute === 'destino' ? line.destino?.name : line[attribute]?.description;
        return value === snapshotFilters[attribute];
      });
      const pending = Number(line.pendingBultos ?? line.availableBultos ?? line.originalBultos ?? 0);
      const statusMatches = !snapshotFilters.estado
        || (snapshotFilters.estado === 'Pendientes' ? pending > 0 : pending <= 0);
      const textMatches = !term || [
        line.sourceExpeditionNo, line.sourceLineNo, line.sourcePedidoNo,
        line.variedad?.description, line.variedad?.code, line.calibre?.description,
        line.proveedor?.name, line.destino?.name, line.confeccion?.description,
        line.envase?.description, line.origen?.description,
      ].some((value) => normalizeText(value).includes(term));
      return attributeFiltersMatch && statusMatches && textMatches;
    });
    const sortValue = (line) => ({
      expedicion: line.sourceExpeditionNo,
      pedido: line.sourcePedidoNo,
      variedad: line.variedad?.description || line.variedad?.code,
      destino: line.destino?.name,
      fecha: line.fechaCarga ? new Date(line.fechaCarga).getTime() : 0,
      palets: Number(line.pallets || 0),
      bultos: Number(line.originalBultos || 0),
      importe: Number(line.estimatedAmount ?? line.estimatedPackagingAmount ?? 0),
      pendientes: Number(line.pendingBultos ?? line.availableBultos ?? line.originalBultos ?? 0),
      asignados: Number(line.assignedBultos || 0),
    }[snapshotSort.key]);
    return filtered.sort((left, right) => {
      const a = sortValue(left);
      const b = sortValue(right);
      const comparison = typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a || '').localeCompare(String(b || ''), 'es', { numeric: true, sensitivity: 'base' });
      return snapshotSort.direction === 'asc' ? comparison : -comparison;
    });
  }, [lines, query, snapshotFilters, snapshotSort]);
  const snapshotFilterOptions = useMemo(() => Object.fromEntries(
    ['variedad', 'calibre', 'confeccion', 'envase', 'origen', 'destino'].map((attribute) => [
      attribute,
      [...new Set(lines.map((line) => (attribute === 'destino' ? line.destino?.name : line[attribute]?.description)).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right, 'es')),
    ]),
  ), [lines]);
  const changeSnapshotSort = (key) => setSnapshotSort((current) => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));
  const visibleLineTotals = useMemo(() => visibleLines.reduce((totals, line) => ({
    assigned: totals.assigned + Number(line.assignedBultos ?? 0),
    pending: totals.pending + Number(line.pendingBultos ?? line.availableBultos ?? line.originalBultos ?? 0),
  }), { assigned: 0, pending: 0 }), [visibleLines]);
  const visibleLineAmounts = useMemo(() => totalAmounts(visibleLines), [visibleLines]);

  const sourceTitle = process?.pedidoCount > 1
    ? `${process.pedidoCount} pedidos`
    : process?.missingPedido ? 'SIN PEDIDO' : process?.pedidoExpedicion || '-';
  const commandInvoices = proposal.filter((invoice) => invoice.blockType !== 'RECHAZO');
  const rejectionBlocks = proposal.filter((invoice) => invoice.blockType === 'RECHAZO' && invoice.lines.length > 0);
  const awaitingManualShipment = rejectionBlocks.some((invoice) => invoice.businessCentralStatus === 'PREPARADA_EN_BC');
  const proposalAmounts = totalAmounts(commandInvoices.flatMap((invoice) => invoice.lines));
  const completedCommandCount = commandInvoices.filter(areInvoiceLinesAssigned).length;
  const pendingCommandCount = commandInvoices.length - completedCommandCount;
  const pendingBcInvoiceCount = commandInvoices.filter((invoice) => invoice.businessCentralStatus !== 'CREADA_EN_BC').length;
  const hasStartedBcSubmission = proposal.some((invoice) => invoice.businessCentralStatus && invoice.businessCentralStatus !== 'NO_ENVIADA');
  const allProposalBlocksCollapsed = proposal.length > 0 && proposal.every((invoice) => openCommandes[invoice.id] === false);
  const allCommandLinesAssigned = commandInvoices.length > 0 && commandInvoices.every((invoice) =>
    invoice.lines.length > 0 && invoice.lines.every((line) => line.matchStatus === 'ASIGNADA'
      && Number(line.assignedBultos) === Number(line.requestedBultos)),
  );
  const allRejectionLinesAssigned = rejectionBlocks.every((invoice) => invoice.lines.every((line) =>
    line.matchStatus === 'ASIGNADA' && Number(line.assignedBultos) === Number(line.requestedBultos)));
  const unresolvedPriceLineCount = commandInvoices.flatMap((invoice) => invoice.lines).filter((line) =>
    line.manualPrice === null && (line.sourcePriceReference === null
      || (line.proposedPrice !== null && Number(line.proposedPrice) !== Number(line.sourcePriceReference) && !line.selectedPriceSource)),
  ).length;
  const pendingAssignmentLineCount = proposal.flatMap((invoice) => invoice.lines).filter((line) =>
    line.matchStatus !== 'ASIGNADA' || Number(line.assignedBultos) !== Number(line.requestedBultos),
  ).length;
  const proposalLineMatchesFilter = (line, blockType = 'COMMANDE') => {
    if (proposalStatusFilter === 'PENDIENTE_ASIGNAR') {
      return line.matchStatus !== 'ASIGNADA' || Number(line.assignedBultos) !== Number(line.requestedBultos);
    }
    if (proposalStatusFilter === 'PENDIENTE_PRECIO') {
      return blockType !== 'RECHAZO' && line.manualPrice === null && (line.sourcePriceReference === null
        || (line.proposedPrice !== null && Number(line.proposedPrice) !== Number(line.sourcePriceReference) && !line.selectedPriceSource));
    }
    return true;
  };
  const hasSuiteInvoices = commandInvoices.some((invoice) => invoice.status === 'CREADA');
  const normalizedProcessStatus = normalizeProcessStatus(process?.status);
  const isInvoiceStage = ['PROPUESTA_VALIDADA', 'FACTURADA'].includes(normalizedProcessStatus) || (normalizedProcessStatus === 'ERROR' && hasSuiteInvoices);
  const canCreateInvoices = ['PROPUESTA_GENERADA', 'ERROR'].includes(process?.status) && !hasSuiteInvoices
    && allCommandLinesAssigned && allRejectionLinesAssigned && unresolvedPriceLineCount === 0;
  const unresolvedInvoiceDestinations = [...new Map(commandInvoices.flatMap((invoice) => invoice.lines)
    .filter((line) => line.destination && !line.internalDestination)
    .map((line) => [normalizeText(line.destination), { key: normalizeText(line.destination), externalDestination: line.destination }]))
    .values()];
  const visibleProposal = proposal.filter((invoice) => {
    const blockLabel = invoice.blockType === 'RECHAZO' ? 'Rechazo' : `Commande ${invoice.commandeNo}`;
    return (!commandeFilter || blockLabel === commandeFilter)
      && invoice.lines.some((line) => proposalLineMatchesFilter(line, invoice.blockType));
  });
  const openInvoiceCreation = async () => {
    setLoadingDestinations(true);
    setError('');
    try {
      const response = await getExpeditionBillingCustomerDestinations({ customerCode: process.customerCode, limit: 500 });
      const destinations = getCollection(response);
      setCustomerDestinations(destinations);
      setDestinationMappings({});
      setCreateInvoicesDialogOpen(true);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron cargar los destinos internos del cliente.'));
    } finally {
      setLoadingDestinations(false);
    }
  };
  const deleteCurrentProcess = async () => {
    if (!process) return;
    setDeleting(true);
    setError('');
    try {
      await deleteExpeditionBillingProcess(process.id);
      navigate('/procesos', { replace: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo eliminar el proceso de facturación.'));
      setDeleting(false);
      setDeleteConfirmationOpen(false);
    }
  };

  return (
    <Box className="billing-page">
      <Breadcrumbs className="billing-breadcrumbs" aria-label="Miga de pan">
        <Typography component={RouterLink} to="/procesos">Procesos de facturación</Typography>
        <Typography color="text.primary">Proceso #{processId}</Typography>
      </Breadcrumbs>
      <PageHeading
        eyebrow="Proceso de facturación"
        title={process ? `Proceso #${process.id}` : 'Proceso de facturación'}
        actions={<Stack direction="row" spacing={1}><Button variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/procesos')}>Volver a procesos</Button>{process && canDeleteProcess(process.status, statuses) ? <Button color="error" variant="outlined" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => setDeleteConfirmationOpen(true)} disabled={deleting}>{deleting ? 'Eliminando...' : 'Eliminar'}</Button> : null}</Stack>}
      >
        {process ? `${process.customerName || process.customerCode} · ${sourceTitle}` : 'Cargando proceso...'}
      </PageHeading>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {bcSendSummary ? <Alert severity={awaitingManualShipment || commandInvoices.some((invoice) => invoice.businessCentralStatus === 'ERROR') ? 'warning' : 'success'} onClose={() => setBcSendSummary('')}>{bcSendSummary}</Alert> : null}
      {awaitingManualShipment ? <Alert severity="info">Registra en Business Central el envio de las lineas ajustadas de la expedicion original y de las expediciones de rechazo indicadas abajo. Despues pulsa Comprobar y facturar.</Alert> : null}
      {loading ? <LinearProgress /> : null}
      <ProposalLineAssignmentDialog
        open={Boolean(assignmentDialog)}
        line={assignmentDialog}
        sourceLines={candidateLines}
        currentPedido={process?.pedidoExpedicion}
        onClose={() => setAssignmentDialog(null)}
        onSave={async (payload) => {
          await saveManualAllocation(assignmentDialog.id, payload);
          setAssignmentDialog(null);
        }}
      />
      <Dialog open={deleteConfirmationOpen} onClose={() => !deleting && setDeleteConfirmationOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar proceso</DialogTitle>
        <DialogContent dividers><Typography>¿Eliminar el proceso #{process?.id}? Sus líneas volverán a estar pendientes de facturar.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteConfirmationOpen(false)} disabled={deleting}>Cancelar</Button><Button color="error" variant="contained" onClick={async () => { await deleteCurrentProcess(); }} disabled={deleting}>{deleting ? 'Eliminando...' : 'Eliminar proceso'}</Button></DialogActions>
      </Dialog>
      <Dialog open={regenerateProposalConfirmationOpen} onClose={() => !generatingProposal && setRegenerateProposalConfirmationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerar propuesta</DialogTitle>
        <DialogContent dividers>
          <Typography>La propuesta se volverá a calcular usando la liquidación analizada. Se perderán las asignaciones manuales realizadas y tendrás que revisarlas de nuevo.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateProposalConfirmationOpen(false)} disabled={generatingProposal}>Cancelar</Button>
          <Button variant="contained" color="warning" startIcon={<RefreshRoundedIcon />} onClick={submitGenerateProposal} disabled={generatingProposal}>
            {generatingProposal ? 'Regenerando...' : 'Regenerar propuesta'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={createInvoicesDialogOpen} onClose={() => !creatingInvoices && setCreateInvoicesDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Validar propuesta</DialogTitle>
        <DialogContent dividers>
          <Typography>Se crearán {commandInvoices.length} facturas, una por cada commande, dentro de la aplicación.</Typography>
          <Typography className="billing-secondary-cell" sx={{ mt: 1 }}>La propuesta quedará cerrada a cambios. No se enviará ninguna información a Dynamics.</Typography>
        </DialogContent>
        <DialogContent dividers>
          <Stack spacing={2}>
            {unresolvedInvoiceDestinations.length > 0 ? <Stack spacing={1}>
              <Typography variant="subtitle2">Destinos sin equivalencia</Typography>
              {unresolvedInvoiceDestinations.map((item) => <Autocomplete
                key={item.key}
                size="small"
                options={customerDestinations}
                value={customerDestinations.find((destination) => String(destination.id) === String(destinationMappings[item.key])) || null}
                onChange={(_, destination) => setDestinationMappings((current) => ({ ...current, [item.key]: destination?.id || '' }))}
                getOptionLabel={(destination) => `${destination.name}${destination.code ? ` (${destination.code})` : ''}${destination.city ? ` · ${destination.city}` : ''}`}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                loading={loadingDestinations}
                noOptionsText="No hay destinos internos para este cliente"
                renderInput={(params) => <TextField {...params} label={`${item.externalDestination} → destino interno`} required />}
              />)}
              <Typography className="billing-secondary-cell">Las selecciones se guardarán en Equivalencias de destino del cliente.</Typography>
            </Stack> : null}
            <TableFrame>
              <Table className="billing-table" size="small">
                <TableHead><TableRow><TableCell>Commande</TableCell><TableCell>Producto interno</TableCell><TableCell>Destino interno</TableCell><TableCell align="right">Bultos</TableCell><TableCell>Expediciones origen</TableCell></TableRow></TableHead>
                <TableBody>{commandInvoices.flatMap((invoice) => invoice.lines.map((line) => {
                  const assignedSources = line.allocations.filter((allocation) => allocation.type !== 'CANDIDATA' && Number(allocation.bultos) > 0);
                  const products = [...new Set(assignedSources.map((allocation) => [
                    allocation.sourceLine.variedad, allocation.sourceLine.calibre, allocation.sourceLine.confeccion,
                    allocation.sourceLine.envase, allocation.sourceLine.origen,
                  ].filter(Boolean).join(' · ')).filter(Boolean))];
                  const selectedDestination = customerDestinations.find((destination) => String(destination.id) === String(destinationMappings[normalizeText(line.destination)]));
                  const sourceDestinations = [...new Set(assignedSources.map((allocation) => allocation.sourceLine.destination?.name).filter(Boolean))];
                  const internalDestination = selectedDestination?.name || line.internalDestination?.name || sourceDestinations.join(', ');
                  return <TableRow key={`${invoice.id}-${line.id}`}>
                    <TableCell>{invoice.commandeNo}</TableCell>
                    <TableCell><Typography className="billing-primary-cell">{products.join(' / ') || 'Sin nomenclatura interna'}</Typography></TableCell>
                    <TableCell>{internalDestination || (line.destination ? 'Pendiente de equivalencia' : 'Sin destino')}</TableCell>
                    <TableCell align="right">{formatNumber(line.requestedBultos, 3)}</TableCell>
                    <TableCell>{assignedSources.map((allocation) => `${allocation.sourceLine.expeditionNo} / ${allocation.sourceLine.lineNo}`).join(', ') || '-'}</TableCell>
                  </TableRow>;
                }))}</TableBody>
              </Table>
            </TableFrame>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateInvoicesDialogOpen(false)} disabled={creatingInvoices}>Cancelar</Button>
          <Button variant="contained" startIcon={<DescriptionRoundedIcon />} onClick={createInvoices} disabled={creatingInvoices || Boolean(savingManualPriceId) || loadingDestinations || unresolvedPriceLineCount > 0 || unresolvedInvoiceDestinations.some((item) => !destinationMappings[item.key])}>{creatingInvoices ? 'Validando...' : 'Validar propuesta'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revertInvoicesDialogOpen} onClose={() => !revertingInvoices && setRevertInvoicesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reabrir propuesta</DialogTitle>
        <DialogContent dividers>
          <Typography>Las facturas locales de este proceso volverán al paso de propuesta. Se conservarán las asignaciones y equivalencias; podrás editar y volver a crear las facturas.</Typography>
          <Typography className="billing-secondary-cell" sx={{ mt: 1 }}>Esta acción solo está disponible antes de enviar el proceso a Business Central.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertInvoicesDialogOpen(false)} disabled={revertingInvoices}>Cancelar</Button>
          <Button variant="contained" color="warning" startIcon={<ReplayRoundedIcon />} onClick={revertInvoiceCreation} disabled={revertingInvoices}>
            {revertingInvoices ? 'Reabriendo...' : 'Reabrir propuesta'}
          </Button>
        </DialogActions>
      </Dialog>

      {process ? (
        <>
          <Paper elevation={0} className="billing-band">
            <Box className="billing-process-overview">
              <Box>
                <Typography className="billing-overview-label">Cliente</Typography>
                <Typography className="billing-overview-value">{process.customerName || process.customerCode}</Typography>
                <Typography className="billing-secondary-cell">{process.customerCode}</Typography>
              </Box>
              <Box>
                <Typography className="billing-overview-label">Pedido expedición</Typography>
                <Typography className="billing-overview-value">{sourceTitle}</Typography>
                {process.pedidoCount > 1 ? <Typography className="billing-secondary-cell">{(process.pedidoExpediciones || []).join(', ')}</Typography> : null}
              </Box>
              <Box>
                <Typography className="billing-overview-label">Estado</Typography>
                <StatusChip status={process.status} statuses={statuses} />
              </Box>
              <Box>
                <Typography className="billing-overview-label">Expediciones incorporadas</Typography>
                <Typography className="billing-overview-value">{formatNumber(process.snapshotExpeditions)} expediciones</Typography>
                <Typography className="billing-secondary-cell">{formatNumber(process.snapshotLines)} líneas · {formatNumber(process.sourceBultosTotal, 2)} bultos</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={0} className="billing-band billing-workflow-band">
            <ProcessWorkflow status={process.status} statuses={statuses} />
          </Paper>

          <Paper elevation={0} className="billing-band billing-data-band">
          <SectionTitle
              title="Liquidación del cliente"
              detail="PDF original y resultado de la lectura automática. Se conserva cada versión para revisión."
              count={`${liquidations.length} ${liquidations.length === 1 ? 'documento' : 'documentos'}`}
            >
              <Button component="label" variant="outlined" startIcon={<UploadFileRoundedIcon />} disabled={uploading}>
                {uploading ? 'Subiendo...' : 'Subir liquidación'}
                <input hidden type="file" accept="application/pdf,.pdf" onChange={uploadLiquidation} />
              </Button>
            </SectionTitle>
            {liquidations.length === 0 ? (
              <EmptyState icon={DescriptionRoundedIcon} title="Aún no hay liquidación" detail="Sube el PDF enviado por el cliente para iniciar la lectura automática." />
            ) : (
              <Stack spacing={1} className="billing-liquidations-list">
                {liquidations.map((liquidation) => (
                  <Box key={liquidation.id} className="billing-liquidation-row">
                    <Box className="billing-liquidation-file"><DescriptionRoundedIcon color="action" /><Box><Typography className="billing-primary-cell">{liquidation.file?.originalName || 'Liquidación'}</Typography><Typography className="billing-secondary-cell">Versión {liquidation.version} · {formatDate(liquidation.submittedAt)}{liquidation.pageCount ? ` · ${liquidation.pageCount} páginas` : ''}</Typography></Box></Box>
                    <Box className="billing-liquidation-status"><Stack direction="row" spacing={0.75} alignItems="center"><Chip size="small" label={liquidation.status === 'ERROR' ? 'Error' : liquidation.status === 'ANALIZADA' ? 'Liquidación analizada' : 'Analizando liquidación'} color={liquidation.status === 'ERROR' ? 'error' : liquidation.status === 'ANALIZADA' ? 'success' : 'warning'} />{['PENDIENTE_ANALISIS', 'ANALIZANDO_OCR', 'ANALIZANDO'].includes(liquidation.status) ? <CircularProgress size={16} aria-label="Analizando liquidación" /> : null}</Stack>{liquidation.confidence ? <Typography className="billing-secondary-cell">Confianza {formatNumber(Number(liquidation.confidence) * 100)}%</Typography> : null}</Box>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Descargar PDF"><span><IconButton size="small" onClick={() => downloadLiquidationFile(liquidation)} disabled={downloadingId === liquidation.id} aria-label={`Descargar ${liquidation.file?.originalName || 'liquidación'}`}>{downloadingId === liquidation.id ? <CircularProgress size={18} /> : <FileDownloadRoundedIcon fontSize="small" />}</IconButton></span></Tooltip>
                      {['ERROR', 'ANALIZADA'].includes(liquidation.status) ? <Tooltip title={liquidation.status === 'ANALIZADA' ? 'Analizar de nuevo' : 'Reintentar análisis'}><span><IconButton size="small" color="primary" disabled={retryingId === liquidation.id} onClick={() => retryLiquidation(liquidation.id)}><ReplayRoundedIcon fontSize="small" /></IconButton></span></Tooltip> : null}
                    </Stack>
                    {liquidation.errorMessage ? <Typography className="billing-liquidation-error">{liquidation.errorMessage}</Typography> : null}
                    {liquidation.priceAuditWarning ? <Alert severity="warning" sx={{ gridColumn: '1 / -1', py: 0 }}>{liquidation.priceAuditWarning}</Alert> : null}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper elevation={0} className="billing-band billing-data-band">
            <SectionTitle
              title={isInvoiceStage ? 'Facturas' : 'Propuesta de facturacion'}
              detail={isInvoiceStage
                ? 'Facturas creadas en Business Central.'
                : 'Bloques por commande y paginas de rechazo. Completa las asignaciones antes de crear las facturas.'}
              count={`${isInvoiceStage ? commandInvoices.length : proposal.length} ${isInvoiceStage ? 'facturas' : proposal.length === 1 ? 'propuesta' : 'propuestas'}`}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {isInvoiceStage ? <>
                  <Tooltip title={hasStartedBcSubmission ? 'No se puede reabrir despues de iniciar un envio a Business Central.' : ''}>
                    <span><Button variant="outlined" color="warning" startIcon={<ReplayRoundedIcon />} onClick={() => setRevertInvoicesDialogOpen(true)} disabled={revertingInvoices || hasStartedBcSubmission}>Reabrir propuesta</Button></span>
                  </Tooltip>
                  <Tooltip title={awaitingManualShipment ? 'Comprueba los envios registrados manualmente y crea las facturas abiertas.' : pendingBcInvoiceCount > 0 ? 'Crea las facturas abiertas en Business Central. No se contabilizaran.' : 'Todas las facturas ya existen en Business Central.'}>
                    <span><Button variant="contained" startIcon={sendingInvoicesToBc ? <CircularProgress size={16} color="inherit" /> : <UploadFileRoundedIcon />} onClick={sendInvoicesToBc} disabled={sendingInvoicesToBc || pendingBcInvoiceCount === 0}>
                      {sendingInvoicesToBc ? 'Facturando...' : pendingBcInvoiceCount ? awaitingManualShipment ? 'Comprobar y facturar' : 'Facturar' : 'Facturada en Navision'}
                    </Button></span>
                  </Tooltip>
                </> : null}
                {proposal.length > 0 ? <SearchableListFilter
                  label="Filtrar bloque"
                  emptyLabel="Todos los bloques"
                  options={[...new Set(proposal.map((invoice) => invoice.blockType === 'RECHAZO' ? 'Rechazo' : `Commande ${invoice.commandeNo}`))].sort()}
                  value={commandeFilter}
                  onChange={setCommandeFilter}
                  sx={{ minWidth: 220 }}
                /> : null}
                {proposal.length > 0 ? <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={proposalStatusFilter}
                  onChange={(_, value) => setProposalStatusFilter(value || '')}
                  aria-label="Filtrar líneas de la propuesta"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="">Todas</ToggleButton>
                  <ToggleButton value="PENDIENTE_ASIGNAR">Pendiente asignar · {pendingAssignmentLineCount}</ToggleButton>
                  <ToggleButton value="PENDIENTE_PRECIO">Pendiente validar precio · {unresolvedPriceLineCount}</ToggleButton>
                </ToggleButtonGroup> : null}
                {proposal.length > 1 ? <Button variant="outlined" startIcon={<ExpandMoreRoundedIcon sx={{ transform: allProposalBlocksCollapsed ? 'none' : 'rotate(180deg)' }} />} onClick={() => setOpenCommandes(Object.fromEntries(proposal.map((invoice) => [invoice.id, allProposalBlocksCollapsed])))}>
                  {allProposalBlocksCollapsed ? 'Expandir todo' : 'Contraer todo'}
                </Button> : null}
                {['LIQUIDACION_ANALIZADA', 'PROPUESTA_GENERADA', 'ERROR'].includes(process.status) && !hasSuiteInvoices ? <Tooltip title={liquidations.some((liquidation) => liquidation.status === 'ANALIZADA') ? '' : 'Analiza una liquidación antes de generar la propuesta.'}>
                  <span><Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={generateProposal} disabled={generatingProposal || !liquidations.some((liquidation) => liquidation.status === 'ANALIZADA')}>
                    {generatingProposal ? 'Generando...' : proposal.length ? 'Regenerar propuesta' : 'Generar propuesta'}
                  </Button></span>
                </Tooltip> : null}
                {['PROPUESTA_GENERADA', 'ERROR'].includes(process.status) && !hasSuiteInvoices ? <Tooltip title={!allCommandLinesAssigned ? 'Asigna todos los bultos de las lineas de commande para continuar.' : unresolvedPriceLineCount > 0 ? `Completa el precio manual o selecciona qué precio usar en las ${unresolvedPriceLineCount} lineas pendientes.` : ''}>
                  <span><Button variant="contained" startIcon={<DescriptionRoundedIcon />} onClick={openInvoiceCreation} disabled={!canCreateInvoices || creatingInvoices || Boolean(savingManualPriceId) || loadingDestinations}>
                    {loadingDestinations ? 'Cargando destinos...' : 'Validar propuesta'}
                  </Button></span>
                </Tooltip> : null}
              </Stack>
            </SectionTitle>
            {commandInvoices.length > 0 ? <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ px: 1.5, py: 1.25, mb: 2, bgcolor: pendingCommandCount === 0 ? '#f0fdf4' : '#fff7ed', border: '1px solid', borderColor: pendingCommandCount === 0 ? '#86efac' : '#fed7aa', borderRadius: '6px' }}>
              <Box>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase' }}>Comandes</Typography>
                <Typography sx={{ fontSize: '1.15rem', lineHeight: 1.2, fontWeight: 700 }}>{commandInvoices.length}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: 'success.dark', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase' }}>Completadas</Typography>
                <Typography sx={{ color: 'success.dark', fontSize: '1.15rem', lineHeight: 1.2, fontWeight: 700 }}>{completedCommandCount}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: pendingCommandCount ? 'warning.dark' : 'text.secondary', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase' }}>Pendientes</Typography>
                <Typography sx={{ color: pendingCommandCount ? 'warning.dark' : 'text.secondary', fontSize: '1.15rem', lineHeight: 1.2, fontWeight: 700 }}>{pendingCommandCount}</Typography>
              </Box>
              <Box sx={{ ml: { md: 'auto' } }}>
                <Typography className="billing-summary-label">Total previsto · todas las commandes</Typography>
                <MoneyAmount value={proposalAmounts.amount} packaging={proposalAmounts.packaging} missing={proposalAmounts.missing} align="flex-start" />
              </Box>
            </Stack> : null}
            {proposal.length === 0 ? (
              <EmptyState icon={SourceRoundedIcon} title="Aún no hay propuesta" detail="Cuando la liquidación esté analizada podrás generar las facturas propuestas." />
            ) : (
              <>
              {visibleProposal.length === 0 ? <EmptyState title="No hay líneas pendientes con este filtro" detail="Cambia el filtro para volver a ver todas las líneas." /> : null}
              <Stack spacing={2}>
                {visibleProposal.map((invoice) => (
                  <Box key={invoice.id} className="billing-proposal-invoice" sx={{ pb: 2, '&:not(:last-child)': { mb: 0.5, borderBottom: '2px solid #dce3eb' } }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ width: '100%', mb: 1, px: 1.25, py: 1.1, bgcolor: areInvoiceLinesAssigned(invoice) ? '#f0fdf4' : '#fff7ed', border: '1px solid', borderColor: areInvoiceLinesAssigned(invoice) ? '#86efac' : '#fed7aa', borderRadius: '6px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                        <Box sx={{ minWidth: { md: 220 }, flexShrink: 0 }}>
                        <Typography className="billing-primary-cell">{invoice.blockType === 'RECHAZO' ? 'Rechazo' : `Commande ${invoice.commandeNo}`}</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mt: 0.5 }}>
                          <Typography sx={{ color: areInvoiceLinesAssigned(invoice) ? 'success.dark' : 'text.primary', fontSize: '1.1rem', lineHeight: 1.1, fontWeight: 700 }}>{invoice.lines.filter((line) => line.matchStatus === 'ASIGNADA').length}/{invoice.lines.length}</Typography>
                          <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>líneas asignadas</Typography>
                        </Stack>
                        </Box>
                        {invoice.blockType !== 'RECHAZO' && !isInvoiceStage ? <ProposalHeaderMetadata invoice={invoice} /> : null}
                      </Box>
                      {invoice.blockType !== 'RECHAZO' ? <Box sx={{ px: { md: 1.5 }, minWidth: 145 }}><Typography className="billing-summary-label">Total commande</Typography><MoneyAmount value={totalAmounts(invoice.lines).amount} packaging={totalAmounts(invoice.lines).packaging} missing={totalAmounts(invoice.lines).missing} align="flex-start" /></Box> : null}
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0, ml: { md: 'auto' } }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip size="small" sx={{ height: 28 }} label={invoice.status} color="primary" variant="outlined" />
                          {isInvoiceStage ? <Tooltip title={invoice.businessCentralStatus === 'PREPARADA_EN_BC' ? 'Registra el envio de esta expedicion de rechazo y de la expedicion original en Business Central.' : invoice.businessCentralError || invoice.businessCentralStatus?.replaceAll('_', ' ') || 'Pendiente de envio'}>
                            <Chip
                              size="small"
                              sx={{ height: 28, maxWidth: 210 }}
                              label={invoice.businessCentralStatus === 'CREADA_EN_BC'
                                ? invoice.blockType === 'RECHAZO'
                                  ? `Expedición ${invoice.businessCentralInvoiceNo || 'creada'}`
                                  : `BC ${invoice.businessCentralInvoiceNo || 'creada'}`
                                : invoice.businessCentralStatus === 'PREPARADA_EN_BC'
                                  ? `Pendiente de enviar ${invoice.businessCentralInvoiceNo || ''}`
                                : invoice.businessCentralStatus === 'ENVIANDO'
                                  ? 'Enviando a BC'
                                  : invoice.businessCentralStatus === 'ERROR'
                                    ? 'Error en BC'
                                    : 'Pendiente BC'}
                              color={invoice.businessCentralStatus === 'CREADA_EN_BC' ? 'success' : invoice.businessCentralStatus === 'PREPARADA_EN_BC' ? 'warning' : invoice.businessCentralStatus === 'ERROR' ? 'error' : 'default'}
                              icon={invoice.businessCentralStatus === 'ENVIANDO' ? <CircularProgress size={12} /> : undefined}
                            />
                          </Tooltip> : null}
                        </Stack>
                        {isInvoiceStage && invoice.blockType === 'COMMANDE' && invoice.businessCentralStatus === 'NO_ENVIADA' ? <Tooltip title="Envía esta factura pendiente a Dynamics.">
                          <span><Button size="small" variant="outlined" color="primary" startIcon={sendingInvoiceId === String(invoice.id) ? <CircularProgress size={14} /> : <ReplayRoundedIcon />} onClick={() => resendInvoiceToBc(invoice)} disabled={Boolean(sendingInvoiceId) || Boolean(sendingInvoicesToBc)} sx={{ whiteSpace: 'nowrap' }}>
                            Enviar a Dynamics
                          </Button></span>
                        </Tooltip> : null}
                        <Button variant="outlined" size="small" sx={{ height: 32, minWidth: 104, bgcolor: 'background.paper', borderColor: 'warning.main', '&:hover': { bgcolor: '#fff1e6', borderColor: 'warning.dark' } }} endIcon={<ExpandMoreRoundedIcon sx={{ transform: openCommandes[invoice.id] !== false ? 'rotate(180deg)' : 'none' }} />} onClick={() => setOpenCommandes((current) => ({ ...current, [invoice.id]: !(current[invoice.id] !== false) }))}>
                          {openCommandes[invoice.id] !== false ? 'Contraer' : 'Expandir'}
                        </Button>
                      </Stack>
                    </Stack>
                    {openCommandes[invoice.id] !== false && invoice.blockType === 'RECHAZO' ? <RejectionBlockDetails block={invoice.blockData} invoice={invoice} onAssign={setAssignmentDialog} canAssign={!isInvoiceStage} lineFilter={proposalLineMatchesFilter} /> : null}
                    {openCommandes[invoice.id] !== false && invoice.blockType !== 'RECHAZO' ? <TableFrame>
                      <Table className="billing-table" size="small" sx={isInvoiceStage
                        ? { '& th:nth-of-type(n+7), & td:nth-of-type(n+7)': { display: 'none' }, '& tbody tr:not(:last-child) td': { borderBottom: '1px solid #cbd5e1' }, '& tbody tr:nth-of-type(odd)': { bgcolor: '#fafbfc' } }
                        : { '& th:nth-of-type(2), & td:nth-of-type(2), & th:nth-of-type(3), & td:nth-of-type(3)': { display: 'none' }, '& tbody tr:not(:last-child) td': { borderBottom: '1px solid #cbd5e1' }, '& tbody tr:nth-of-type(odd)': { bgcolor: '#fafbfc' } }}>
                        <TableHead><TableRow><TableCell>{isInvoiceStage ? 'Producto' : 'Producto cliente'}</TableCell><TableCell>Fecha BL</TableCell><TableCell>Destino</TableCell><TableCell align="right">Bultos</TableCell><TableCell>Precios</TableCell><TableCell align="right">Importe línea</TableCell><TableCell>Resultado</TableCell><TableCell>Origen / candidatos</TableCell><TableCell align="right">Acción</TableCell></TableRow></TableHead>
                        <TableBody>{invoice.lines.filter((line) => proposalLineMatchesFilter(line, invoice.blockType)).map((line) => <TableRow key={line.id}>
                          <TableCell><Typography className="billing-primary-cell">{line.description || '-'}</Typography><Typography className="billing-secondary-cell">{[line.product?.variedad, line.product?.calibre, line.product?.origen, line.product?.confeccion, line.product?.envase].filter(Boolean).join(' · ')}{line.product?.unitsPerPackage ? ` · Unidades ${formatNumber(line.product.unitsPerPackage, 3)}` : ''}</Typography></TableCell>
                          <TableCell>{line.fechaBl ? formatDate(line.fechaBl) : '-'}</TableCell>
                          <TableCell>{isInvoiceStage ? line.internalDestination?.name || line.destination || '-' : <>{line.destination || '-'}{line.internalDestination ? <Typography className="billing-secondary-cell">{line.internalDestination.name}</Typography> : null}</>}</TableCell>
                          <TableCell align="right">{isInvoiceStage ? formatNumber(line.assignedBultos, 3) : `${formatNumber(line.assignedBultos, 3)} / ${formatNumber(line.requestedBultos, 3)}`}</TableCell>
                          <TableCell sx={{ minWidth: 210 }}>
                            {isInvoiceStage ? <Stack spacing={0.25} alignItems="flex-end">
                              <Typography sx={{ fontWeight: 700 }}>{line.finalPrice === null ? '-' : formatNumber(line.finalPrice, 4)}</Typography>
                              <Typography className="billing-secondary-cell">Precio aplicado · {line.manualPrice !== null ? 'Manual' : line.selectedPriceSource === 'EXPEDICION' ? 'Expedición' : 'Precio cliente'} · {priceUnitLabel(line.customerPriceUnit)}</Typography>
                            </Stack> : <>
                            <Stack spacing={0.25}>
                              <Typography className="billing-secondary-cell">Precio cliente ({line.customerPriceUnit === 'KILOS' ? '€/kg' : line.customerPriceUnit === 'UNIDADES_VARIABLES' ? '€/unidad variable' : 'unidad sin definir'}): <strong>{line.proposedPrice === null ? '-' : formatNumber(line.proposedPrice, 4)}</strong></Typography>
                              <Typography className="billing-secondary-cell">Expedición convertida: <strong>{line.sourcePriceReference === null ? '-' : formatNumber(line.sourcePriceReference, 4)}</strong>{line.customerPriceUnit ? ` (${line.customerPriceUnit === 'KILOS' ? '€/kg' : '€/unidad variable'})` : ''}</Typography>
                              {!isInvoiceStage ? <FormControl size="small" sx={{ minWidth: 190, mt: 0.5 }}>
                                <InputLabel>Unidad del precio cliente</InputLabel>
                                <Select label="Unidad del precio cliente" value={line.customerPriceUnit || ''} onChange={(event) => selectProposalPriceUnit(line, event.target.value)}>
                                  <MenuItem value=""><em>Seleccionar unidad</em></MenuItem>
                                  <MenuItem value="KILOS">Kilos</MenuItem>
                                  <MenuItem value="UNIDADES_VARIABLES">Unidades variables</MenuItem>
                                </Select>
                              </FormControl> : null}
                              {line.priceComparisonWarning ? <Typography sx={{ color: 'warning.dark', fontSize: '0.7rem' }}>{line.priceComparisonWarning}</Typography> : null}
                              {!isInvoiceStage && line.sourcePriceReference === null && line.customerPriceUnit ? <ManualProposalPriceEditor line={line} onSave={saveManualProposalPrice} disabled={savingManualPriceId === line.id} /> : null}
                              {line.manualPrice !== null && line.manualPrice !== undefined ? <Typography className="billing-secondary-cell">Precio manual guardado: <strong>{formatNumber(line.manualPrice, 4)}</strong> ({line.customerPriceUnit === 'KILOS' ? '€/kg' : '€/unidad variable'})</Typography> : null}
                              {line.proposedPrice !== null && line.sourcePriceReference !== null && Number(line.proposedPrice) !== Number(line.sourcePriceReference) ? <>
                                <Chip size="small" color="warning" label="Diferencia de precio" sx={{ alignSelf: 'flex-start', height: 22, mt: 0.25 }} />
                                {!isInvoiceStage ? <RadioGroup row value={line.selectedPriceSource || ''} onChange={(event) => selectProposalPrice(line, event.target.value)} sx={{ gap: 0.5 }}>
                                  <FormControlLabel value="PDF" control={<Radio size="small" sx={{ p: 0.4 }} />} label="Usar precio cliente" sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.72rem' } }} />
                                  <FormControlLabel value="EXPEDICION" control={<Radio size="small" sx={{ p: 0.4 }} />} label="Usar expedición" sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.72rem' } }} />
                                </RadioGroup> : <Typography className="billing-secondary-cell">Precio aplicado: {line.selectedPriceSource === 'EXPEDICION' ? 'Expedición' : 'Precio cliente'}</Typography>}
                              </> : null}
                            </Stack>
                            </>}
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 150 }}><MoneyAmount value={line.estimatedAmount} packaging={line.estimatedPackagingAmount} missing={line.estimatedAmount == null ? 1 : 0} /></TableCell>
                          <TableCell><Chip size="small" label={line.matchStatus.replaceAll('_', ' ')} color={line.matchStatus === 'ASIGNADA' ? 'success' : line.matchStatus === 'REQUIERE_REVISION' ? 'warning' : 'default'} /></TableCell>
                          <TableCell>{line.allocations.length ? line.allocations.map((allocation, index) => <Box key={`${allocation.type}-${index}`} className="billing-secondary-cell"><Typography component="span" className="billing-secondary-cell">{allocation.type === 'CANDIDATA' ? 'Candidato' : `${formatNumber(allocation.bultos, 3)} bultos`}: {allocation.sourceLine.expeditionNo} / {allocation.sourceLine.lineNo}</Typography><Typography className="billing-secondary-cell">{[allocation.sourceLine.variedad, allocation.sourceLine.calibre, allocation.sourceLine.origen, allocation.sourceLine.confeccion, allocation.sourceLine.envase].filter(Boolean).join(' · ')}</Typography>{allocation.type !== 'CANDIDATA' && allocation.sourceLine.packagingLines?.map((packaging) => {
                            const parentPackages = Number(allocation.sourceLine.originalBultos || 0);
                            const allocatedQuantity = parentPackages > 0 ? Number(packaging.quantity || 0) * Number(allocation.bultos || 0) / parentPackages : Number(packaging.quantity || 0);
                            const netUnitPrice = Number(packaging.unitPrice || 0) * (1 - Number(packaging.discountPct || 0) / 100);
                            return <Typography key={`${packaging.lineNo}-${packaging.itemNo}`} className="billing-secondary-cell">Envase · {packaging.itemNo} {packaging.description || ''} · {formatNumber(allocatedQuantity, 3)} ud. · {packaging.locationCode || 'Sin almacén'} · {formatNumber(netUnitPrice, 4)} €/ud. · {formatNumber(allocatedQuantity * netUnitPrice, 2)} €</Typography>;
                          })}</Box>) : '-'}</TableCell>
                          <TableCell align="right">{!isInvoiceStage ? <Tooltip title="Asignar expedición manualmente"><IconButton size="small" color="primary" onClick={() => setAssignmentDialog(line)}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}</TableCell>
                        </TableRow>)}</TableBody>
                      </Table>
                    </TableFrame> : null}
                  </Box>
                ))}
              </Stack>
              </>
            )}
          </Paper>

          <Paper elevation={0} className="billing-band billing-data-band">
            <SectionTitle
              title="Líneas incorporadas"
              detail={`Bultos asignados: ${formatNumber(visibleLineTotals.assigned, 2)} · Pendientes: ${formatNumber(visibleLineTotals.pending, 2)}. Datos incorporados desde Business Central.`}
              count={`${formatNumber(visibleLines.length)} líneas`}
            >
              <TextField
                className="billing-search-field"
                placeholder="Buscar expedición, producto o destino"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
              />
            </SectionTitle>
            <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap sx={{ mb: 1.5 }}>
              {[
                ['variedad', 'Variedad'], ['calibre', 'Calibre'], ['confeccion', 'Confección'],
                ['envase', 'Envase'], ['origen', 'Origen'], ['destino', 'Destino'],
              ].map(([attribute, label]) => <SearchableListFilter
                key={attribute}
                label={label}
                emptyLabel="Todos"
                options={snapshotFilterOptions[attribute]}
                value={snapshotFilters[attribute]}
                onChange={(value) => setSnapshotFilters((current) => ({ ...current, [attribute]: value }))}
                sx={{ minWidth: 155, flex: '1 1 155px' }}
              />)}
              <SearchableListFilter
                label="Estado de asignación"
                emptyLabel="Todos"
                options={['Pendientes', 'Completa']}
                value={snapshotFilters.estado}
                onChange={(value) => setSnapshotFilters((current) => ({ ...current, estado: value }))}
                sx={{ minWidth: 180, flex: '1 1 180px' }}
              />
            </Stack>
            <Box className="billing-table-total"><Typography>Total de líneas visibles</Typography><MoneyAmount value={visibleLineAmounts.amount} packaging={visibleLineAmounts.packaging} missing={visibleLineAmounts.missing} /></Box>

            {visibleLines.length === 0 ? (
              <EmptyState icon={Inventory2RoundedIcon} title="No hay líneas para mostrar" detail={query ? 'Prueba con otro término de búsqueda.' : 'Este proceso no contiene líneas importadas.'} />
            ) : (
              <TableFrame>
                <Table className="billing-table billing-lines-table" size="small">
                  <TableHead>
                    <TableRow>
                      <SortableBillingCell label="Expedición" sortKey="expedicion" sort={snapshotSort} onSort={changeSnapshotSort} />
                      <SortableBillingCell label="Pedido origen" sortKey="pedido" sort={snapshotSort} onSort={changeSnapshotSort} />
                      <SortableBillingCell label="Producto" sortKey="variedad" sort={snapshotSort} onSort={changeSnapshotSort} />
                      <SortableBillingCell label="Destino" sortKey="destino" sort={snapshotSort} onSort={changeSnapshotSort} />
                      <SortableBillingCell label="Fecha carga" sortKey="fecha" sort={snapshotSort} onSort={changeSnapshotSort} />
                      <SortableBillingCell label="Palets" sortKey="palets" sort={snapshotSort} onSort={changeSnapshotSort} align="right" />
                      <SortableBillingCell label="Bultos origen" sortKey="bultos" sort={snapshotSort} onSort={changeSnapshotSort} align="right" />
                      <SortableBillingCell label="Importe origen" sortKey="importe" sort={snapshotSort} onSort={changeSnapshotSort} align="right" />
                      <SortableBillingCell label="Estado de asignación" sortKey="pendientes" sort={snapshotSort} onSort={changeSnapshotSort} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleLines.map((line) => <BillingSnapshotLine key={line.id} line={line} />)}
                  </TableBody>
                </Table>
              </TableFrame>
            )}
          </Paper>
        </>
      ) : null}
    </Box>
  );
}

function ProcessWorkflow({ status }) {
  const currentStatus = normalizeProcessStatus(status);
  const currentIndex = PROCESS_STATUS_STEPS.findIndex((step) => step.code === currentStatus);
  return (
    <Box className="billing-workflow" aria-label="Estados del proceso">
      {PROCESS_STATUS_STEPS.map((step, index) => (
        <React.Fragment key={step.label}>
          <Box className={`billing-workflow-step ${currentIndex >= 0 && index <= currentIndex ? 'billing-workflow-step-active' : ''}`}>
            <Box className="billing-workflow-icon">{index < currentIndex || (currentStatus === 'FACTURADA' && index === currentIndex) ? <CheckCircleRoundedIcon fontSize="small" /> : index + 1}</Box>
            <Typography>{step.label}</Typography>
          </Box>
          {index < PROCESS_STATUS_STEPS.length - 1 ? <Box className="billing-workflow-connector" /> : null}
        </React.Fragment>
      ))}
      {currentStatus === 'ERROR' ? <Chip size="small" color="error" label="Error" /> : null}
    </Box>
  );
}

function ProposalLineAssignmentDialog({ open, line, sourceLines, currentPedido, onClose, onSave }) {
  const [allocations, setAllocations] = useState({});
  const [search, setSearch] = useState('');
  const [attributeFilters, setAttributeFilters] = useState({ variedad: '', calibre: '', confeccion: '', envase: '', origen: '' });
  const [sourcePedidoFilter, setSourcePedidoFilter] = useState('');
  const [registerMappings, setRegisterMappings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [matchPromptOpen, setMatchPromptOpen] = useState(false);
  const [overwritePending, setOverwritePending] = useState(false);

  useEffect(() => {
    if (!open || !line) return;
    setSearch('');
    const hasCurrentPedido = sourceLines.some((source) => source.sourceProcessPedido === currentPedido);
    setSourcePedidoFilter(currentPedido && hasCurrentPedido ? currentPedido : '');
    const assignedAllocations = (line.allocations || []).filter((allocation) => allocation.type !== 'CANDIDATA');
    const defaults = Object.fromEntries(['variedad', 'calibre', 'confeccion', 'envase', 'origen'].map((attribute) => {
      const values = [...new Set(assignedAllocations.map((allocation) => allocation.sourceLine?.[attribute]).filter(Boolean))];
      if (values.length === 1) return [attribute, values[0]];
      const externalValue = line.product?.[attribute];
      const exactInternalValue = sourceLines.some((source) => source[attribute]?.description === externalValue);
      return [attribute, exactInternalValue ? externalValue : ''];
    }));
    setAttributeFilters(defaults);
    setRegisterMappings(assignedAllocations.length <= 1);
    setError('');
    setAllocations(Object.fromEntries((line.allocations || [])
      .filter((allocation) => allocation.type !== 'CANDIDATA')
      .map((allocation) => [allocation.sourceLine.id, allocation.bultos])));
  }, [currentPedido, line, open, sourceLines]);

  const filteredLines = useMemo(() => {
    const term = normalizeText(search);
    return sourceLines.filter((source) => {
      const pedidoMatch = !sourcePedidoFilter || source.sourceProcessPedido === sourcePedidoFilter;
      const attributeMatch = ['variedad', 'calibre', 'confeccion', 'envase', 'origen'].every((attribute) =>
        !attributeFilters[attribute] || source[attribute]?.description === attributeFilters[attribute]);
      const searchMatch = !term || [
        source.sourceExpeditionNo, source.sourceLineNo, source.sourcePedidoNo,
        source.variedad?.description, source.calibre?.description, source.origen?.description,
        source.confeccion?.description, source.envase?.description, source.destino?.name,
      ].some((value) => normalizeText(value).includes(term));
      return pedidoMatch && attributeMatch && searchMatch;
    });
  }, [attributeFilters, search, sourcePedidoFilter, sourceLines]);
  const sourcePedidoOptions = useMemo(() => [...new Set(
    sourceLines.map((source) => source.sourceProcessPedido).filter(Boolean),
  )].sort((left, right) => left.localeCompare(right, 'es', { numeric: true })), [sourceLines]);
  const filterOptions = useMemo(() => Object.fromEntries(['variedad', 'calibre', 'confeccion', 'envase', 'origen'].map((attribute) => [
    attribute,
    [...new Set(sourceLines.map((source) => source[attribute]?.description).filter(Boolean))].sort((left, right) => left.localeCompare(right)),
  ])), [sourceLines]);

  const selectedIds = Object.entries(allocations).filter(([, value]) => Number(value) > 0).map(([id]) => id);
  const assigned = selectedIds.reduce((total, id) => total + Number(allocations[id] || 0), 0);
  const requested = Number(line?.requestedBultos || 0);
  const conflictWarning = error.includes('equivalencias activas');

  const toggle = (source) => {
    const isSelected = Number(allocations[source.id] || 0) > 0;
    const originalAllocation = (line?.allocations || [])
      .filter((allocation) => allocation.type !== 'CANDIDATA' && allocation.sourceLine?.id === source.id)
      .reduce((total, allocation) => total + Number(allocation.bultos || 0), 0);
    const assignable = Math.max(0, Number(source.availableBultos) - Math.max(0, Number(source.assignedBultos) - originalAllocation));
    setRegisterMappings(selectedIds.length + (isSelected ? -1 : 1) === 1);
    setAllocations((current) => {
      if (Number(current[source.id] || 0) > 0) {
        const next = { ...current };
        delete next[source.id];
        return next;
      }
      const remaining = Math.max(0, requested - Object.values(current).reduce((total, value) => total + Number(value || 0), 0));
      return { ...current, [source.id]: Math.min(remaining, assignable) };
    });
  };

  const submit = async (overwriteMappings = false, matchSimilarUnassigned = false) => {
    setSaving(true);
    setError('');
    try {
      await onSave({
        allocations: selectedIds.map((processLineId) => ({ processLineId, bultos: Number(allocations[processLineId]) })),
        registerMappings,
        overwriteMappings,
        matchSimilarUnassigned,
      });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar la asignación.'));
    } finally {
      setSaving(false);
    }
  };

  const requestSubmit = (overwriteMappings = false) => {
    setOverwritePending(overwriteMappings);
    const selectedProcessIds = new Set(selectedIds.map((id) => sourceLines.find((source) => source.id === id)?.sourceProcessId).filter(Boolean));
    if (selectedIds.length > 0 && selectedProcessIds.size === 1) {
      setMatchPromptOpen(true);
      return;
    }
    submit(overwriteMappings, false);
  };

  const answerMatchPrompt = (matchSimilarUnassigned) => {
    setMatchPromptOpen(false);
    submit(overwritePending, matchSimilarUnassigned);
  };

  return (
    <>
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xl" fullWidth>
      <DialogTitle>Asignar expedición a la línea de liquidación</DialogTitle>
      <DialogContent dividers>
        {line ? <Stack spacing={2}>
          <Box><Typography className="billing-primary-cell">{line.description || '-'}</Typography><Typography className="billing-secondary-cell">{[line.product?.variedad, line.product?.calibre, line.product?.origen, line.product?.confeccion, line.product?.envase].filter(Boolean).join(' · ')}{line.product?.unitsPerPackage ? ` · Unidades ${formatNumber(line.product.unitsPerPackage, 3)}` : ''} · {line.destination || '-'}</Typography></Box>
          {error ? <Alert severity={conflictWarning ? 'warning' : 'error'}>{error}</Alert> : null}
          <SearchableListFilter
            label="Pedido"
            emptyLabel="Todos los pedidos"
            options={sourcePedidoOptions}
            value={sourcePedidoFilter}
            onChange={setSourcePedidoFilter}
            sx={{ minWidth: 220, maxWidth: 420 }}
          />
          <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
            {[
              ['variedad', 'Variedad'], ['calibre', 'Calibre'], ['confeccion', 'Confección'], ['envase', 'Envase'], ['origen', 'Origen'],
            ].map(([attribute, label]) => <SearchableListFilter
              key={attribute}
              label={label}
              emptyLabel="Todas"
              options={filterOptions[attribute]}
              value={attributeFilters[attribute]}
              onChange={(value) => setAttributeFilters((current) => ({ ...current, [attribute]: value }))}
              sx={{ minWidth: 165, flex: '1 1 165px' }}
            />)}
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ md: 'center' }}>
            <Typography>Bultos seleccionados: <strong>{formatNumber(assigned, 3)} / {formatNumber(requested, 3)}</strong></Typography>
            <TextField size="small" placeholder="Buscar expedición, variedad, confección..." value={search} onChange={(event) => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
          </Stack>
          <TableFrame>
            <Table className="billing-table" size="small">
              <TableHead><TableRow><TableCell padding="checkbox" /><TableCell>Expedición</TableCell><TableCell>Producto</TableCell><TableCell>Destino</TableCell><TableCell align="right">Disponibles</TableCell><TableCell align="right">Bultos a asignar</TableCell></TableRow></TableHead>
              <TableBody>{filteredLines.map((source) => {
                const value = Number(allocations[source.id] || 0);
                const originalAllocation = (line?.allocations || [])
                  .filter((allocation) => allocation.type !== 'CANDIDATA' && allocation.sourceLine?.id === source.id)
                  .reduce((total, allocation) => total + Number(allocation.bultos || 0), 0);
                const assignable = Math.max(0, Number(source.availableBultos) - Math.max(0, Number(source.assignedBultos) - originalAllocation));
                const available = Math.max(0, assignable - value);
                return <TableRow key={source.id} hover selected={value > 0}>
                  <TableCell padding="checkbox"><Checkbox checked={value > 0} onChange={() => toggle(source)} /></TableCell>
                  <TableCell>
                    <Typography className="billing-primary-cell">{source.sourceExpeditionNo}{source.priceType ? ` (${source.priceType})` : ''}</Typography>
                    <Typography className="billing-secondary-cell">Proceso #{source.sourceProcessId || '-'} · Pedido {source.sourceProcessPedido || source.sourcePedidoNo || '-'} · Línea {source.sourceLineNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className="billing-primary-cell">{source.variedad?.description || '-'}</Typography>
                    <Typography className="billing-secondary-cell">{[source.calibre?.description, source.origen?.description, source.confeccion?.description, source.envase?.description].filter(Boolean).join(' · ')}</Typography>
                    {source.packagingLines?.map((packaging) => {
                      const netUnitPrice = Number(packaging.unitPrice || 0) * (1 - Number(packaging.discountPct || 0) / 100);
                      return <Typography key={`${packaging.lineNo}-${packaging.itemNo}`} className="billing-secondary-cell" color="warning.dark">
                        Incluye {packaging.description || packaging.itemNo} · {formatNumber(packaging.quantity, 3)} ud. · {formatNumber(netUnitPrice, 4)} €/ud.
                      </Typography>;
                    })}
                  </TableCell>
                  <TableCell>{source.destino?.name || '-'}</TableCell>
                  <TableCell align="right">{formatNumber(available, 3)}</TableCell>
                  <TableCell align="right"><TextField size="small" type="number" disabled={value <= 0} value={value || ''} onChange={(event) => setAllocations((current) => ({ ...current, [source.id]: event.target.value }))} inputProps={{ min: 0, max: assignable, step: 'any', style: { textAlign: 'right', width: 90 } }} /></TableCell>
                </TableRow>;
              })}</TableBody>
            </Table>
          </TableFrame>
          <Stack direction="row" alignItems="center" spacing={1}><Checkbox checked={registerMappings} disabled={selectedIds.length !== 1} onChange={(event) => setRegisterMappings(event.target.checked)} /><Typography>Registrar equivalencias con la línea seleccionada</Typography></Stack>
          {registerMappings && selectedIds.length !== 1 ? <Typography color="warning.main" variant="body2">Selecciona una única línea para registrar equivalencias.</Typography> : null}
        </Stack> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        {conflictWarning ? <Button color="warning" variant="contained" disabled={saving} onClick={() => requestSubmit(true)}>Sobrescribir equivalencias</Button> : null}
        <Button variant="contained" disabled={saving || assigned > requested || (registerMappings && selectedIds.length !== 1)} onClick={() => requestSubmit(false)}>{saving ? 'Guardando...' : 'Guardar asignación'}</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={matchPromptOpen} onClose={() => !saving && setMatchPromptOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Emparejar líneas iguales</DialogTitle>
      <DialogContent dividers>
        <Typography>¿Deseas emparejar también las líneas iguales que aún no están asignadas?</Typography>
        <Typography className="billing-secondary-cell" sx={{ mt: 1 }}>Se usarán expediciones compatibles del mismo pedido que has seleccionado y se respetarán sus bultos disponibles.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => answerMatchPrompt(false)} disabled={saving}>No, guardar solo esta asignación</Button>
        <Button variant="contained" onClick={() => answerMatchPrompt(true)} disabled={saving}>Sí, emparejar líneas iguales</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

function RejectionBlockDetails({ block, invoice, onAssign, canAssign, lineFilter = () => true }) {
  const lines = Array.isArray(block?.lines) ? block.lines : [];
  return (
    <TableFrame>
      <Table className="billing-table" size="small">
        <TableHead><TableRow><TableCell>Linea de rechazo</TableCell><TableCell>Pedido origen</TableCell><TableCell align="right">Bultos asignados</TableCell><TableCell>Resultado</TableCell><TableCell>Expediciones origen / candidatos</TableCell><TableCell align="right">Accion</TableCell></TableRow></TableHead>
        <TableBody>{(invoice?.lines || []).map((line, index) => ({ line, index })).filter(({ line }) => lineFilter(line, 'RECHAZO')).map(({ line, index }) => {
          const extractedLine = lines[index];
          return <TableRow key={line.id}>
            <TableCell>
              <Typography className="billing-primary-cell">{line.description || extractedLine?.description || '-'}</Typography>
              <Typography className="billing-secondary-cell">{[line.product?.variedad, line.product?.calibre, line.product?.origen, line.product?.confeccion, line.product?.envase].filter(Boolean).join(' · ')}</Typography>
            </TableCell>
            <TableCell>{extractedLine?.orderNumber || '-'}</TableCell>
            <TableCell align="right">{formatNumber(line.assignedBultos, 3)} / {formatNumber(line.requestedBultos, 3)}</TableCell>
            <TableCell><Chip size="small" label={line.matchStatus.replaceAll('_', ' ')} color={line.matchStatus === 'ASIGNADA' ? 'success' : line.matchStatus === 'REQUIERE_REVISION' ? 'warning' : 'default'} /></TableCell>
            <TableCell>{line.allocations.length ? line.allocations.map((allocation, allocationIndex) => <Box key={`${allocation.type}-${allocationIndex}`}>
              <Typography className="billing-secondary-cell">{allocation.type === 'CANDIDATA' ? 'Candidato' : `${formatNumber(allocation.bultos, 3)} bultos`}: {allocation.sourceLine.expeditionNo} / {allocation.sourceLine.lineNo}</Typography>
              <Typography className="billing-secondary-cell">{[allocation.sourceLine.variedad, allocation.sourceLine.calibre, allocation.sourceLine.origen, allocation.sourceLine.confeccion, allocation.sourceLine.envase].filter(Boolean).join(' · ')}</Typography>
            </Box>) : '-'}</TableCell>
            <TableCell align="right">{canAssign ? <Tooltip title="Asignar expedicion manualmente"><IconButton size="small" color="primary" onClick={() => onAssign(line)}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}</TableCell>
          </TableRow>;
        })}</TableBody>
      </Table>
    </TableFrame>
  );
}

function SearchableListFilter({ label, options, value, onChange, emptyLabel = 'Todas', sx }) {
  return (
    <Autocomplete
      size="small"
      options={options}
      value={value || null}
      onChange={(_, option) => onChange(option || '')}
      isOptionEqualToValue={(option, selected) => option === selected}
      noOptionsText="Sin resultados"
      sx={sx}
      renderInput={(params) => <TextField {...params} label={label} placeholder={emptyLabel} />}
    />
  );
}

function SortableBillingCell({ label, sortKey, sort, onSort, align = 'left' }) {
  const active = sort.key === sortKey;
  return (
    <TableCell align={align} sortDirection={active ? sort.direction : false}>
      <TableSortLabel
        active={active}
        direction={active ? sort.direction : 'asc'}
        onClick={() => onSort(sortKey)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function BillingSnapshotLine({ line }) {
  const productBits = [
    line.confeccion?.description || line.confeccion?.code,
    line.calibre?.description || line.calibre?.code,
    line.origen?.description || line.origen?.code,
    line.envase?.description || line.envase?.code,
  ].filter(Boolean);
  const pending = line.pendingBultos ?? line.availableBultos ?? line.originalBultos;
  const assigned = line.assignedBultos ?? 0;
  return (
    <TableRow hover>
      <TableCell><Typography className="billing-primary-cell">{line.sourceExpeditionNo || '-'}</Typography><Typography className="billing-secondary-cell">Línea {formatNumber(line.sourceLineNo)}</Typography></TableCell>
      <TableCell><Typography className="billing-primary-cell">{line.sourcePedidoNo || '-'}</Typography><Typography className="billing-secondary-cell">Línea {formatNumber(line.sourcePedidoLineNo)}</Typography></TableCell>
      <TableCell>
        <Typography className="billing-primary-cell">{line.variedad?.description || line.variedad?.code || '-'}</Typography>
        <Typography className="billing-secondary-cell">{productBits.join(' · ') || '-'}</Typography>
        {line.packagingLines?.map((packaging) => {
          const unitPrice = Number(packaging.unitPrice || 0) * (1 - Number(packaging.discountPct || 0) / 100);
          return <Typography key={`${packaging.lineNo}-${packaging.itemNo}`} className="billing-secondary-cell">
            Envase: {packaging.itemNo} {packaging.description || ''} · {formatNumber(packaging.quantity, 3)} ud. · {formatNumber(unitPrice, 4)} €/ud.
          </Typography>;
        })}
      </TableCell>
      <TableCell><Typography className="billing-primary-cell">{line.destino?.name || line.destino?.code || '-'}</Typography><Typography className="billing-secondary-cell">{line.destino?.code || ''}</Typography></TableCell>
      <TableCell>{formatDate(line.fechaCarga)}</TableCell>
      <TableCell align="right">{formatNumber(line.pallets, 2)}</TableCell>
      <TableCell align="right" className="billing-number-strong">{formatNumber(line.originalBultos, 2)}</TableCell>
      <TableCell align="right"><MoneyAmount value={line.estimatedAmount} packaging={line.estimatedPackagingAmount} missing={line.estimatedAmount == null ? 1 : 0} /></TableCell>
      <TableCell align="right">
        <Stack spacing={0.4} alignItems="flex-end" sx={{ minWidth: 140 }}>
          <Typography sx={{ color: pending > 0 ? 'warning.dark' : 'success.dark', fontSize: '0.8rem', fontWeight: 800 }}>
            {pending > 0 ? `${formatNumber(pending, 2)} pendientes` : 'Completa'}
          </Typography>
          <Typography className="billing-secondary-cell">{formatNumber(assigned, 2)} asignados de {formatNumber(line.originalBultos, 2)}</Typography>
          <LinearProgress
            variant="determinate"
            color={pending > 0 ? 'warning' : 'success'}
            value={line.originalBultos > 0 ? Math.min(100, (Number(assigned) / Number(line.originalBultos)) * 100) : 0}
            sx={{ width: 112, height: 5, borderRadius: 4 }}
          />
          {Number(line.discardedBultos || 0) > 0 ? <Typography className="billing-secondary-cell">{formatNumber(line.discardedBultos, 2)} descartados</Typography> : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export function BillingConfigurationPage() {
  const [models, setModels] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState('');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [modelDialog, setModelDialog] = useState(null);
  const [configurationPage, setConfigurationPage] = useState(0);
  const [configurationMeta, setConfigurationMeta] = useState({ total: 0 });

  const loadConfiguration = useCallback(async ({ page = configurationPage, search = query, includeModels = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const requests = [
        getExpeditionBillingCustomerConfigurations({
          page: page + 1,
          limit: CONFIGURATION_PAGE_SIZE,
          search,
        }),
      ];
      if (includeModels || models.length === 0) requests.unshift(getExpeditionBillingModels());
      const responses = await Promise.all(requests);
      const modelsResponse = includeModels || models.length === 0 ? responses.shift() : null;
      const configurationsResponse = responses[0];
      const loadedModels = modelsResponse ? getCollection(modelsResponse) : models;
      const loadedConfigurations = getCollection(configurationsResponse);
      if (modelsResponse) setModels(loadedModels);
      setConfigurations(loadedConfigurations);
      setConfigurationMeta(getMeta(configurationsResponse));
      setDrafts(Object.fromEntries(loadedConfigurations.map((configuration) => [
        configuration.customerCode,
        {
          active: Boolean(configuration.active),
          billingModelId: configuration.billingModel?.id || '',
          notes: configuration.notes || '',
        },
      ])));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo cargar la configuración de facturación.'));
    } finally {
      setLoading(false);
    }
  }, [configurationPage, models, query]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadConfiguration({ includeModels: models.length === 0 });
    }, query ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [loadConfiguration, models.length, query]);

  const activeModels = models.filter((model) => model.active);
  const changeDraft = (customerCode, field, value) => {
    setDrafts((current) => ({
      ...current,
      [customerCode]: {
        ...(current[customerCode] || { active: false, billingModelId: '', notes: '' }),
        [field]: value,
      },
    }));
  };

  const saveCustomer = async (configuration) => {
    const draft = drafts[configuration.customerCode] || {};
    setSavingCode(configuration.customerCode);
    setError('');
    try {
      const response = await updateExpeditionBillingCustomerConfiguration(configuration.customerCode, draft);
      const saved = response?.data || response;
      setConfigurations((current) => current.map((item) => item.customerCode === saved.customerCode ? saved : item));
      setDrafts((current) => ({
        ...current,
        [saved.customerCode]: {
          active: saved.active,
          billingModelId: saved.billingModel?.id || '',
          notes: saved.notes || '',
        },
      }));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar la configuración del cliente.'));
    } finally {
      setSavingCode('');
    }
  };

  const saveModel = async (form) => {
    const payload = {
      code: form.code,
      name: form.name,
      description: form.description || null,
      active: form.active,
      displayOrder: Number(form.displayOrder || 0),
    };
    const response = form.id
      ? await updateExpeditionBillingModel(form.id, payload)
      : await createExpeditionBillingModel(payload);
    const saved = response?.data || response;
    setModels((current) => {
      const next = form.id
        ? current.map((model) => model.id === saved.id ? saved : model)
        : [...current, saved];
      return next.sort((left, right) => left.displayOrder - right.displayOrder || left.name.localeCompare(right.name));
    });
    setModelDialog(null);
  };

  return (
    <Box className="billing-page">
      <PageHeading
        eyebrow="Facturación de expediciones"
        title="Configuración de facturación"
      >
        Elige qué clientes se pueden facturar y qué modelo se aplicará a sus nuevos procesos.
      </PageHeading>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper elevation={0} className="billing-band billing-data-band">
        <SectionTitle
          title="Modelos de facturación"
          detail="Cada modelo define cómo se revisarán los nuevos procesos."
          count={`${formatNumber(models.length)} modelos`}
        >
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setModelDialog({})}>Nuevo modelo</Button>
        </SectionTitle>
        {loading ? <LinearProgress /> : null}
        {!loading && models.length === 0 ? <EmptyState title="No hay modelos definidos" detail="Crea el primer modelo para poder activar clientes." /> : null}
        {models.length > 0 ? (
          <TableFrame className="billing-models-table-frame">
            <Table className="billing-table billing-models-table" size="small">
              <TableHead><TableRow><TableCell>Código</TableCell><TableCell>Modelo</TableCell><TableCell>Descripción</TableCell><TableCell>Clientes</TableCell><TableCell>Estado</TableCell><TableCell align="right">Acción</TableCell></TableRow></TableHead>
              <TableBody>
                {models.map((model) => (
                  <TableRow key={model.id} hover>
                    <TableCell><Typography className="billing-primary-cell">{model.code}</Typography></TableCell>
                    <TableCell><Typography className="billing-primary-cell">{model.name}</Typography></TableCell>
                    <TableCell>{model.description || '-'}</TableCell>
                    <TableCell>{formatNumber(model.customersCount)}</TableCell>
                    <TableCell><Chip label={model.active ? 'Activo' : 'Inactivo'} size="small" className={model.active ? 'billing-status-chip billing-status-success' : 'billing-status-chip'} /></TableCell>
                    <TableCell align="right"><Button variant="text" size="small" onClick={() => setModelDialog(model)}>Editar</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableFrame>
        ) : null}
      </Paper>

      <Paper elevation={0} className="billing-band billing-data-band">
        <SectionTitle
          title="Clientes habilitados"
          detail="Solo los clientes activos con un modelo seleccionado aparecerán en Expediciones pendientes."
          count={`${formatNumber(configurationMeta.total || 0)} clientes`}
        >
          <TextField
            className="billing-search-field"
            placeholder="Buscar cliente"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setConfigurationPage(0);
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          />
        </SectionTitle>
        {loading ? <LinearProgress /> : null}
        {!loading && configurations.length === 0 ? <EmptyState title="No hay clientes disponibles" detail="Los clientes activos de logística aparecerán al sincronizar sus datos maestros." /> : null}
        {configurations.length > 0 ? (
          <TableFrame>
            <Table className="billing-table billing-configurations-table" size="small">
              <TableHead><TableRow><TableCell>Cliente</TableCell><TableCell>Activo</TableCell><TableCell>Modelo de facturación</TableCell><TableCell>Notas</TableCell><TableCell align="right">Acción</TableCell></TableRow></TableHead>
              <TableBody>
                {configurations.map((configuration) => {
                  const draft = drafts[configuration.customerCode] || { active: false, billingModelId: '', notes: '' };
                  const invalidActiveModel = draft.active && !draft.billingModelId;
                  return (
                    <TableRow key={configuration.customerCode} hover>
                      <TableCell><Typography className="billing-primary-cell">{configuration.customerName}</Typography><Typography className="billing-secondary-cell">{configuration.customerCode}</Typography></TableCell>
                      <TableCell><Switch checked={draft.active} onChange={(event) => changeDraft(configuration.customerCode, 'active', event.target.checked)} inputProps={{ 'aria-label': `Activar ${configuration.customerName}` }} /></TableCell>
                      <TableCell>
                        <FormControl size="small" className="billing-model-select" error={invalidActiveModel}>
                          <InputLabel>Modelo</InputLabel>
                          <Select label="Modelo" value={draft.billingModelId} onChange={(event) => changeDraft(configuration.customerCode, 'billingModelId', event.target.value)}>
                            <MenuItem value=""><em>Sin modelo</em></MenuItem>
                            {activeModels.map((model) => <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell><TextField size="small" placeholder="Notas" value={draft.notes} onChange={(event) => changeDraft(configuration.customerCode, 'notes', event.target.value)} fullWidth /></TableCell>
                      <TableCell align="right"><Button variant="outlined" size="small" disabled={savingCode === configuration.customerCode || invalidActiveModel} onClick={() => saveCustomer(configuration)}>{savingCode === configuration.customerCode ? 'Guardando...' : 'Guardar'}</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableFrame>
        ) : null}
        {configurationMeta.total > CONFIGURATION_PAGE_SIZE ? (
          <TablePagination
            component="div"
            count={configurationMeta.total}
            page={configurationPage}
            rowsPerPage={CONFIGURATION_PAGE_SIZE}
            rowsPerPageOptions={[CONFIGURATION_PAGE_SIZE]}
            labelRowsPerPage="Filas"
            onPageChange={(_, nextPage) => setConfigurationPage(nextPage)}
          />
        ) : null}
      </Paper>

      <BillingModelDialog
        model={modelDialog}
        open={modelDialog !== null}
        onClose={() => setModelDialog(null)}
        onSave={saveModel}
      />
    </Box>
  );
}

function BillingModelDialog({ model, open, onClose, onSave }) {
  const [form, setForm] = useState({ code: '', name: '', description: '', active: true, displayOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      id: model?.id,
      code: model?.code || '',
      name: model?.name || '',
      description: model?.description || '',
      active: model?.active ?? true,
      displayOrder: model?.displayOrder ?? 0,
    });
  }, [model, open]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar el modelo de facturación.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{model?.id ? 'Editar modelo de facturación' : 'Nuevo modelo de facturación'}</DialogTitle>
      <DialogContent dividers>
        <Box className="billing-model-form">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Código" value={form.code} onChange={(event) => setField('code', event.target.value.toUpperCase())} required inputProps={{ maxLength: 40 }} />
          <TextField label="Nombre" value={form.name} onChange={(event) => setField('name', event.target.value)} required inputProps={{ maxLength: 120 }} />
          <TextField label="Orden" type="number" value={form.displayOrder} onChange={(event) => setField('displayOrder', event.target.value)} inputProps={{ min: 0 }} />
          <TextField className="billing-model-description" label="Descripción" value={form.description} onChange={(event) => setField('description', event.target.value)} multiline minRows={2} />
          <Stack direction="row" alignItems="center" spacing={1}><Switch checked={form.active} onChange={(event) => setField('active', event.target.checked)} /><Typography>Activo</Typography></Stack>
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button variant="contained" disabled={saving || !form.code.trim() || !form.name.trim()} onClick={submit}>{saving ? 'Guardando...' : 'Guardar modelo'}</Button></DialogActions>
    </Dialog>
  );
}

const PRODUCT_ATTRIBUTE_TABS = [
  { value: 'VARIEDAD', label: 'Variedad' },
  { value: 'CONFECCION', label: 'Confección' },
  { value: 'CALIBRE', label: 'Calibre' },
  { value: 'ORIGEN', label: 'Origen' },
  { value: 'ENVASE', label: 'Envase' },
];

function productAttributeLabel(attribute) {
  return PRODUCT_ATTRIBUTE_TABS.find((item) => item.value === attribute)?.label || attribute || 'atributo';
}

export function BillingCorrespondencesPage() {
  const [customers, setCustomers] = useState([]);
  const [customerCode, setCustomerCode] = useState('');
  const [tab, setTab] = useState('VARIEDAD');
  const [mappings, setMappings] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncExecution, setSyncExecution] = useState(null);
  const [error, setError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [dialog, setDialog] = useState(null);

  const loadCustomers = useCallback(async () => {
    try {
      const response = await getExpeditionBillingMappingCustomers();
      const availableCustomers = getCollection(response);
      setCustomers(availableCustomers);
      setCustomerCode((current) => current && availableCustomers.some((customer) => customer.customerCode === current)
        ? current
        : availableCustomers[0]?.customerCode || '');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron cargar los clientes activos.'));
    }
  }, []);

  const loadMappings = useCallback(async () => {
    if (!customerCode) {
      setMappings([]);
      setMeta({ total: 0 });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = tab !== 'DESTINOS'
        ? await getExpeditionBillingProductAttributeMappings({ customerCode, attribute: tab, page: page + 1, limit: 25, search: query, ...(activeFilter ? { active: activeFilter } : {}) })
        : await getExpeditionBillingDestinationMappings({ customerCode, page: page + 1, limit: 25, search: query, ...(activeFilter ? { active: activeFilter } : {}) });
      setMappings(getCollection(response));
      setMeta(getMeta(response));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron cargar las correspondencias.'));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, customerCode, page, query, tab]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => {
    const timer = window.setTimeout(loadMappings, query ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [loadMappings, query]);

  const selectedCustomer = customers.find((customer) => customer.customerCode === customerCode);
  const resetListing = (nextTab = tab) => {
    setTab(nextTab);
    setPage(0);
    setQuery('');
  };

  const saveMapping = async (form) => {
    const response = form.type !== 'DESTINOS'
      ? (form.id
        ? await updateExpeditionBillingProductAttributeMapping(form.id, form.payload)
        : await createExpeditionBillingProductAttributeMapping({ ...form.payload, customerCode }))
      : (form.id
        ? await updateExpeditionBillingDestinationMapping(form.id, form.payload)
        : await createExpeditionBillingDestinationMapping({ ...form.payload, customerCode }));
    const saved = response?.data || response;
    setMappings((current) => form.id
      ? current.map((mapping) => mapping.id === saved.id ? saved : mapping)
      : [saved, ...current]);
    setMeta((current) => ({ ...current, total: form.id ? current.total : Number(current.total || 0) + 1 }));
    setDialog(null);
    await loadMappings();
  };

  const syncMasterData = async () => {
    setSyncing(true);
    setError('');
    setSyncMessage('');
    try {
      const response = await syncExpeditionBillingMasterData();
      const result = response?.data || response;
      setSyncExecution({ executionId: result.executionId, status: result.status || 'PENDING' });
      setSyncMessage(`Sincronizacion iniciada. Ejecucion ${result.executionId || ''} en cola.`.trim());
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron sincronizar los maestros de facturación.'));
    } finally {
      setSyncing(false);
    }
  };

  const refreshSyncExecution = useCallback(async () => {
    if (!syncExecution?.executionId) return;
    try {
      const response = await getExpeditionBillingMasterDataSyncExecution(syncExecution.executionId);
      const execution = response?.data || response;
      setSyncExecution(execution);
      if (['SUCCESS', 'PARTIAL'].includes(execution.status)) {
        setSyncMessage(`Sincronizacion finalizada: ${formatNumber(execution.recordsCreated)} nuevos, ${formatNumber(execution.recordsUpdated)} actualizados y ${formatNumber(execution.recordsFailed)} con error.`);
      }
      if (execution.status === 'FAILED') {
        setError(execution.errorSummary || 'La sincronizacion de maestros ha fallado.');
      }
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo consultar el estado de la sincronizacion.'));
    }
  }, [syncExecution?.executionId]);

  useEffect(() => {
    if (!syncExecution?.executionId || !['PENDING', 'RUNNING'].includes(syncExecution.status)) return undefined;
    const timer = window.setTimeout(refreshSyncExecution, 2000);
    return () => window.clearTimeout(timer);
  }, [refreshSyncExecution, syncExecution?.executionId, syncExecution?.status]);

  const syncInProgress = syncing || ['PENDING', 'RUNNING'].includes(syncExecution?.status);

  return (
    <Box className="billing-page">
      <PageHeading
        eyebrow="Facturación de expediciones"
        title="Equivalencias del cliente"
        actions={(
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={syncInProgress ? <CircularProgress size={15} /> : <RefreshRoundedIcon />} onClick={syncMasterData} disabled={syncInProgress}>
              {syncInProgress ? (syncExecution?.status === 'RUNNING' ? 'Sincronizando...' : 'En cola...') : 'Sincronizar productos y destinos'}
            </Button>
          </Stack>
        )}
      >
        Equivalencias confirmadas entre los nombres que usa el cliente y nuestros productos o destinos.
      </PageHeading>

      {error ? <Alert severity="error" className="billing-alert">{error}</Alert> : null}
      {syncMessage ? <Alert severity="success" className="billing-alert" onClose={() => setSyncMessage('')}>{syncMessage}</Alert> : null}
      {syncExecution && ['PENDING', 'RUNNING'].includes(syncExecution.status) ? <Alert severity="info" className="billing-alert">Sincronizacion de maestros {syncExecution.status === 'RUNNING' ? 'en curso' : 'pendiente de iniciar'}.</Alert> : null}

      <Paper elevation={0} className="billing-band billing-data-band">
        <Box className="billing-mapping-toolbar">
          <FormControl size="small" className="billing-mapping-customer-select">
            <InputLabel>Cliente</InputLabel>
            <Select
              label="Cliente"
              value={customerCode}
              onChange={(event) => {
                setCustomerCode(event.target.value);
                setPage(0);
                setQuery('');
              }}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.customerCode} value={customer.customerCode}>
                  {customer.customerName} ({customer.customerCode})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedCustomer?.billingModel ? <Chip label={selectedCustomer.billingModel.name} size="small" className="billing-status-chip billing-status-pending" /> : null}
        </Box>
        <Tabs value={tab} onChange={(_, value) => resetListing(value)} className="billing-mapping-tabs" variant="scrollable" scrollButtons="auto">
          {PRODUCT_ATTRIBUTE_TABS.map((item) => <Tab key={item.value} value={item.value} label={item.label} />)}
          <Tab value="DESTINOS" label="Destinos" />
        </Tabs>
      </Paper>

      <Paper elevation={0} className="billing-band billing-data-band">
        <SectionTitle
          title={tab === 'DESTINOS' ? 'Equivalencias de destino' : `Equivalencias de ${productAttributeLabel(tab).toLowerCase()}`}
          detail={tab !== 'DESTINOS'
            ? `Relaciona el texto utilizado por el cliente con nuestra ${productAttributeLabel(tab).toLowerCase()} interna.`
            : 'Relaciona el destino escrito por el cliente con uno de sus destinos registrados.'}
          count={`${formatNumber(meta.total || 0)} reglas`}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
            <TextField
              className="billing-search-field"
              placeholder={tab === 'DESTINOS' ? 'Buscar destino' : `Buscar ${productAttributeLabel(tab).toLowerCase()}`}
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="mapping-active-filter-label">Estado</InputLabel>
              <Select
                labelId="mapping-active-filter-label"
                label="Estado"
                value={activeFilter}
                onChange={(event) => { setActiveFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="true">Activas</MenuItem>
                <MenuItem value="false">Inactivas</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={!customerCode} onClick={() => setDialog({ type: tab })}>
              Nueva equivalencia
            </Button>
          </Stack>
        </SectionTitle>
        {loading ? <LinearProgress /> : null}
        {!loading && !customerCode ? <EmptyState icon={HubRoundedIcon} title="No hay clientes habilitados" detail="Activa un cliente y asígnale un modelo desde Configuración de facturación." /> : null}
        {!loading && customerCode && mappings.length === 0 ? (
          <EmptyState
            icon={HubRoundedIcon}
            title={query || activeFilter ? 'No hay resultados' : 'Aún no hay equivalencias'}
            detail={query || activeFilter
              ? 'Prueba con otra búsqueda o cambia el estado seleccionado.'
              : 'Crea la primera equivalencia para reutilizarla en las siguientes liquidaciones.'}
          />
        ) : null}
        {mappings.length > 0 ? (
          <TableFrame className="billing-mappings-table-frame">
            {tab !== 'DESTINOS' ? <ProductAttributeMappingsTable mappings={mappings} onEdit={(mapping) => setDialog({ type: tab, mapping })} /> : <DestinationMappingsTable mappings={mappings} onEdit={(mapping) => setDialog({ type: 'DESTINOS', mapping })} />}
          </TableFrame>
        ) : null}
        {meta.total > 25 ? <TablePagination component="div" count={meta.total} page={page} rowsPerPage={25} rowsPerPageOptions={[25]} labelRowsPerPage="Filas" onPageChange={(_, nextPage) => setPage(nextPage)} /> : null}
      </Paper>

      <BillingMappingDialog
        open={Boolean(dialog)}
        type={dialog?.type}
        mapping={dialog?.mapping}
        customerCode={customerCode}
        onClose={() => setDialog(null)}
        onSave={saveMapping}
      />
    </Box>
  );
}

function ProductAttributeMappingsTable({ mappings, onEdit }) {
  return (
    <Table className="billing-table billing-mappings-table" size="small">
      <TableHead><TableRow><TableCell>Atributo</TableCell><TableCell>Valor indicado por cliente</TableCell><TableCell>Unidad de precio</TableCell><TableCell>Valor interno</TableCell><TableCell>Código interno</TableCell><TableCell>Estado</TableCell><TableCell>Notas</TableCell><TableCell align="right">Acción</TableCell></TableRow></TableHead>
      <TableBody>{mappings.map((mapping) => (
        <TableRow hover key={mapping.id}>
          <TableCell><Chip size="small" label={productAttributeLabel(mapping.attribute)} className="billing-status-chip billing-status-pending" /></TableCell>
          <TableCell>
            <Typography className="billing-primary-cell">{mapping.externalValue}</Typography>
            <Typography className="billing-secondary-cell">{mapping.attribute === 'CONFECCION' && mapping.externalUnits ? `Unidades por bulto: ${mapping.externalUnits}` : 'Manual'}</Typography>
          </TableCell>
          <TableCell>{mapping.attribute === 'CONFECCION' ? priceUnitLabel(mapping.customerPriceUnit) : '-'}</TableCell>
          <TableCell><Typography className="billing-primary-cell">{mapping.internalCode ? mapping.internalDescription : 'Sin equivalencia interna'}</Typography></TableCell>
          <TableCell>
            {mapping.internalCode || '-'}
            {mapping.attribute === 'CALIBRE' && mapping.internalCalibreGroupCode
              ? <Typography className="billing-secondary-cell">Familia {mapping.internalCalibreGroupCode}</Typography>
              : null}
          </TableCell>
          <TableCell><Chip size="small" label={mapping.active ? 'Activa' : 'Inactiva'} className={mapping.active ? 'billing-status-chip billing-status-success' : 'billing-status-chip'} /></TableCell>
          <TableCell>{mapping.notes || '-'}</TableCell>
          <TableCell align="right"><Button size="small" onClick={() => onEdit(mapping)}>Editar</Button></TableCell>
        </TableRow>
      ))}</TableBody>
    </Table>
  );
}

function DestinationMappingsTable({ mappings, onEdit }) {
  return (
    <Table className="billing-table billing-mappings-table" size="small">
      <TableHead><TableRow><TableCell>Destino indicado por cliente</TableCell><TableCell>Destino interno</TableCell><TableCell>Ciudad / país</TableCell><TableCell>Estado</TableCell><TableCell>Notas</TableCell><TableCell align="right">Acción</TableCell></TableRow></TableHead>
      <TableBody>{mappings.map((mapping) => (
        <TableRow hover key={mapping.id}>
          <TableCell><Typography className="billing-primary-cell">{mapping.externalDestination}</Typography><Typography className="billing-secondary-cell">Manual</Typography></TableCell>
          <TableCell><Typography className="billing-primary-cell">{mapping.destination?.name || '-'}</Typography><Typography className="billing-secondary-cell">{mapping.destination?.code || ''}</Typography></TableCell>
          <TableCell>{[mapping.destination?.city, mapping.destination?.country].filter(Boolean).join(' / ') || '-'}</TableCell>
          <TableCell><Chip size="small" label={mapping.active ? 'Activa' : 'Inactiva'} className={mapping.active ? 'billing-status-chip billing-status-success' : 'billing-status-chip'} /></TableCell>
          <TableCell>{mapping.notes || '-'}</TableCell>
          <TableCell align="right"><Button size="small" onClick={() => onEdit(mapping)}>Editar</Button></TableCell>
        </TableRow>
      ))}</TableBody>
    </Table>
  );
}

/* Replaced by the attribute-aware dialog below. */
/*
function LegacyBillingMappingDialog({ open, type, mapping, customerCode, onClose, onSave }) {
  const isDestination = type === 'DESTINOS';
  const [form, setForm] = useState({ externalValue: '', selectedCode: '', selectedDescription: '', notes: '', active: true });
  const [options, setOptions] = useState([]);
  const [optionSearch, setOptionSearch] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setOptionSearch('');
    setForm({
      externalValue: mapping?.externalValue || mapping?.externalDestination || '',
      selectedCode: mapping?.internalCode || mapping?.destination?.id || '',
      selectedDescription: mapping?.internalDescription || mapping?.destination?.name || '',
      notes: mapping?.notes || '',
      active: mapping?.active ?? true,
    });
  }, [mapping, open]);

  useEffect(() => {
    if (!open || !type || !customerCode) return;
    const timer = window.setTimeout(async () => {
      setLoadingOptions(true);
      try {
        const response = !isDestination
          ? await getExpeditionBillingProductAttributeValues({ attribute: type, limit: 500, search: optionSearch })
          : await getExpeditionBillingCustomerDestinations({ customerCode, limit: 500, search: optionSearch });
        setOptions(getCollection(response));
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, 'No se pudieron cargar las opciones.'));
      } finally {
        setLoadingOptions(false);
      }
    }, optionSearch ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [customerCode, isDestination, open, optionSearch, type]);

  const submit = async () => {
    const payload = !isDestination
      ? { attribute: type, externalValue: form.externalValue.trim(), internalCode: form.selectedCode || null, internalDescription: form.selectedDescription, notes: form.notes.trim() || null, active: form.active }
      : { externalDestination: form.externalValue.trim(), customerDestinationId: form.selectedCode, notes: form.notes.trim() || null, active: form.active };
    setSaving(true);
    setError('');
    try {
      await onSave({ type, id: mapping?.id, payload });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar la equivalencia.'));
    } finally {
      setSaving(false);
    }
  };

  const selectedLabel = !isDestination
    ? options.find((option) => option.code === form.selectedCode)?.label || form.selectedDescription
    : options.find((option) => option.id === form.selectedCode)?.name || form.selectedDescription;
  const valid = form.externalValue.trim() && form.selectedCode && form.selectedDescription;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{mapping?.id ? 'Editar equivalencia' : `Nueva equivalencia de ${isDestination ? 'destino' : productAttributeLabel(type).toLowerCase()}`}</DialogTitle>
      <DialogContent dividers>
        <Box className="billing-mapping-form">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label={type === 'products' ? 'Descripción del cliente' : 'Destino indicado por cliente'} value={form.externalValue} onChange={(event) => setForm((current) => ({ ...current, externalValue: event.target.value }))} required inputProps={{ maxLength: 500 }} />
          {type === 'products' ? <TextField label="Referencia del cliente" value={form.externalReference} onChange={(event) => setForm((current) => ({ ...current, externalReference: event.target.value }))} inputProps={{ maxLength: 120 }} /> : <Box />}
          <TextField className="billing-mapping-option-search" label={type === 'products' ? 'Buscar producto interno' : 'Buscar destino interno'} value={optionSearch} onChange={(event) => setOptionSearch(event.target.value)} InputProps={{ endAdornment: loadingOptions ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null }} />
          <FormControl required>
            <InputLabel>{type === 'products' ? 'Producto interno' : 'Destino interno'}</InputLabel>
            <Select label={type === 'products' ? 'Producto interno' : 'Destino interno'} value={form.selectedId} onChange={(event) => setForm((current) => ({ ...current, selectedId: event.target.value }))} renderValue={(value) => selectedLabel || value}>
              {options.map((option) => <MenuItem key={option.id} value={option.id}>{type === 'products' ? option.label : `${option.name} (${option.code})`}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField className="billing-mapping-notes" label="Notas" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} multiline minRows={2} />
          <Stack direction="row" alignItems="center" spacing={1}><Switch checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} /><Typography>Regla activa</Typography></Stack>
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button variant="contained" disabled={saving || !valid} onClick={submit}>{saving ? 'Guardando...' : 'Guardar equivalencia'}</Button></DialogActions>
    </Dialog>
  );
}

*/
function BillingMappingDialog({ open, type, mapping, customerCode, onClose, onSave }) {
  const isDestination = type === 'DESTINOS';
  const isCalibre = type === 'CALIBRE';
  const isConfeccion = type === 'CONFECCION';
  const [form, setForm] = useState({ externalValue: '', externalUnits: '', customerPriceUnit: '', selectedCode: '', selectedDescription: '', selectedCalibreGroupCode: '', notes: '', active: true });
  const [options, setOptions] = useState([]);
  const [calibreGroups, setCalibreGroups] = useState([]);
  const [optionSearch, setOptionSearch] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingCalibreGroups, setLoadingCalibreGroups] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setOptionSearch('');
    setForm({
      externalValue: mapping?.externalValue || mapping?.externalDestination || '',
      externalUnits: mapping?.externalUnits ?? '',
      customerPriceUnit: mapping?.customerPriceUnit || '',
      selectedCode: mapping?.internalCode || mapping?.destination?.id || '',
      selectedDescription: mapping?.internalDescription || mapping?.destination?.name || '',
      selectedCalibreGroupCode: mapping?.internalCalibreGroupCode || '',
      notes: mapping?.notes || '',
      active: mapping?.active ?? true,
    });
  }, [mapping, open]);

  useEffect(() => {
    if (!open || !isCalibre) return undefined;
    let active = true;
    setLoadingCalibreGroups(true);
    getExpeditionBillingProductAttributeValues({ attribute: 'VARIEDAD', limit: 500 })
      .then((response) => {
        if (!active) return;
        const groups = new Map();
        getCollection(response).forEach((variety) => {
          if (variety.calibreGroupCode && !groups.has(variety.calibreGroupCode)) {
            groups.set(variety.calibreGroupCode, { code: variety.calibreGroupCode, label: `Familia ${variety.calibreGroupCode}` });
          }
        });
        setCalibreGroups([...groups.values()].sort((left, right) => left.code.localeCompare(right.code)));
      })
      .catch((requestError) => {
        if (active) setError(getApiErrorMessage(requestError, 'No se pudieron cargar las familias de calibre.'));
      })
      .finally(() => {
        if (active) setLoadingCalibreGroups(false);
      });
    return () => { active = false; };
  }, [isCalibre, open]);

  useEffect(() => {
    if (!open || !type || !customerCode) return;
    if (isCalibre && !form.selectedCalibreGroupCode) {
      setOptions([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      setLoadingOptions(true);
      try {
        const response = isDestination
          ? await getExpeditionBillingCustomerDestinations({ customerCode, limit: 500, search: optionSearch })
          : await getExpeditionBillingProductAttributeValues({
              attribute: type,
              limit: 500,
              search: optionSearch,
              ...(isCalibre ? { calibreGroupCode: form.selectedCalibreGroupCode } : {}),
            });
        setOptions(getCollection(response));
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, 'No se pudieron cargar las opciones.'));
      } finally {
        setLoadingOptions(false);
      }
    }, optionSearch ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [customerCode, form.selectedCalibreGroupCode, isCalibre, isDestination, open, optionSearch, type]);

  const selectOption = (option) => {
    const selectedCode = isDestination ? option?.id || '' : option?.code || '';
    setForm((current) => ({
      ...current,
      selectedCode,
      selectedDescription: isDestination ? option?.name || '' : option?.description || '',
      selectedCalibreGroupCode: !isDestination && isCalibre ? option?.calibreGroupCode || current.selectedCalibreGroupCode : '',
    }));
  };

  const submit = async () => {
    const payload = isDestination
      ? { externalDestination: form.externalValue.trim(), customerDestinationId: form.selectedCode, notes: form.notes.trim() || null, active: form.active }
      : {
          attribute: type,
          externalValue: form.externalValue.trim(),
          externalUnits: isConfeccion ? Number(form.externalUnits) : null,
          customerPriceUnit: isConfeccion ? form.customerPriceUnit || null : null,
          internalCode: form.selectedCode || null,
          internalCalibreGroupCode: isCalibre ? form.selectedCalibreGroupCode || null : null,
          internalDescription: form.selectedDescription,
          notes: form.notes.trim() || null,
          active: form.active,
        };
    setSaving(true);
    setError('');
    try {
      await onSave({ type, id: mapping?.id, payload });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo guardar la equivalencia.'));
    } finally {
      setSaving(false);
    }
  };

  const valid = Boolean(
    form.externalValue.trim()
    && form.selectedCode
    && form.selectedDescription
    && (!isCalibre || form.selectedCalibreGroupCode)
    && (!isConfeccion || (Number(form.externalUnits) > 0 && Boolean(form.customerPriceUnit))),
  );
  const targetLabel = isDestination ? 'Destino interno' : `${productAttributeLabel(type)} interna`;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{mapping?.id ? 'Editar equivalencia' : `Nueva equivalencia de ${isDestination ? 'destino' : productAttributeLabel(type).toLowerCase()}`}</DialogTitle>
      <DialogContent dividers>
        <Box className="billing-mapping-form">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label={isDestination ? 'Destino indicado por cliente' : `${productAttributeLabel(type)} indicada por cliente`} value={form.externalValue} onChange={(event) => setForm((current) => ({ ...current, externalValue: event.target.value }))} required inputProps={{ maxLength: 500 }} />
          {isConfeccion ? <TextField label="Unidades por bulto del cliente" value={form.externalUnits} onChange={(event) => setForm((current) => ({ ...current, externalUnits: event.target.value }))} required type="number" inputProps={{ min: 0.001, step: 'any' }} /> : <Box />}
          {isConfeccion ? <FormControl required>
            <InputLabel>Unidad de precio del cliente</InputLabel>
            <Select label="Unidad de precio del cliente" value={form.customerPriceUnit} onChange={(event) => setForm((current) => ({ ...current, customerPriceUnit: event.target.value }))}>
              <MenuItem value="KILOS">Kilos</MenuItem>
              <MenuItem value="UNIDADES_VARIABLES">Unidades variables</MenuItem>
            </Select>
          </FormControl> : <Box />}
          {isCalibre ? (
            <Autocomplete
              className="billing-mapping-option-search"
              options={calibreGroups}
              loading={loadingCalibreGroups}
              value={calibreGroups.find((group) => group.code === form.selectedCalibreGroupCode) || null}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              getOptionLabel={(option) => option.label}
              onChange={(_, group) => setForm((current) => ({
                ...current,
                selectedCalibreGroupCode: group?.code || '',
                selectedCode: '',
                selectedDescription: '',
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Familia de calibres"
                  required
                  InputProps={{
                    ...(params.InputProps || {}),
                    endAdornment: <>{loadingCalibreGroups ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps?.endAdornment || null}</>,
                  }}
                />
              )}
            />
          ) : null}
          <Autocomplete
            className="billing-mapping-option-search"
            options={options}
            loading={loadingOptions}
            value={options.find((option) => (isDestination
              ? option.id === form.selectedCode
              : option.code === form.selectedCode && (type !== 'CALIBRE' || option.calibreGroupCode === form.selectedCalibreGroupCode))) || null}
            isOptionEqualToValue={(option, value) => (isDestination
              ? option.id === value.id
              : option.code === value.code && (type !== 'CALIBRE' || option.calibreGroupCode === value.calibreGroupCode))}
            getOptionLabel={(option) => isDestination ? `${option.name} (${option.code})` : option.label || option.description || ''}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') setOptionSearch(value);
            }}
            onChange={(_, option) => selectOption(option)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={isCalibre ? 'Calibre interno' : targetLabel}
                required
                disabled={isCalibre && !form.selectedCalibreGroupCode}
                InputProps={{
                  ...(params.InputProps || {}),
                  endAdornment: <>{loadingOptions ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps?.endAdornment || null}</>,
                }}
              />
            )}
          />
          <TextField className="billing-mapping-notes" label="Notas" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} multiline minRows={2} />
          <Stack direction="row" alignItems="center" spacing={1}><Switch checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} /><Typography>Regla activa</Typography></Stack>
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button variant="contained" disabled={saving || !valid} onClick={submit}>{saving ? 'Guardando...' : 'Guardar equivalencia'}</Button></DialogActions>
    </Dialog>
  );
}
